import React, { useState, useEffect, useRef } from "react";
import { UTILS, INS, CRITUTILS } from "../config/API";
import { useNavigate } from 'react-router-dom';
import RadioField from "../components/RadioField";
import CheckboxField from "../components/CheckboxField";
import SignatureCanvas from "react-signature-canvas";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { fetchProfileData } from '../utils/profile';
import jsPDF from 'jspdf';
import ConfirmationModal from "../components/ConfirmationModal";
import autoTable from "jspdf-autotable";
import critLogo from "../assets/icons/critLogo.png";

const TaxReturnForm = ({ parseData, onItemSelect }) => {
    const navigate = useNavigate();

    // Get current date and calculate last month (same as DistributorReportForm)
    const getDefaultDate = () => {
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth();

        let lastMonth = currentMonth - 1;
        let year = currentYear;

        if (lastMonth < 0) {
            lastMonth = 11;
            year = currentYear - 1;
        }

        const monthNames = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];

        return {
            month: monthNames[lastMonth],
            year: year.toString()
        };
    };

    const defaultDate = getDefaultDate();

    // Month options for dropdown (same as DistributorReportForm)
    const monthOptions = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    // Year options - past 3 years, current year, and next 3 years (7 years total)
    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 7 }, (_, i) => (currentYear - 3 + i).toString());

    const [formSections, setFormSections] = useState({
        taxrevenue: { fields: [], title: "", signature: null, signatureText: "", fileUpload: null, fileUploadText: "", fileTypes: "" }
    });
    const [isLoading, setIsLoading] = useState(true);
    const inputRef = useRef(null);

    const [inputValue, setInputValue] = useState("");
    const [emailError, setEmailError] = useState("");
    const [isAPILoading, setIsAPILoading] = useState(false);
    const [formData, setFormData] = useState({
        tribalLicenseNo: '',
        reportingPeriodMonth: defaultDate.month,
        reportingPeriodYear: defaultDate.year
    });
    const [zipCodes, setZipCodes] = useState([]);
    const [showZipDropdown, setShowZipDropdown] = useState(false);
    const [options, setOptions] = useState([]);
    const [picklistCache, setPicklistCache] = useState({});
    const [signature, setSignature] = useState(null);
    const [files, setFiles] = useState([{ id: 1, file: null, type: "" }]);
    const [showSignature, setShowSignature] = useState(false);
    const [signatureText, setSignatureText] = useState("");
    const [showFileUpload, setShowFileUpload] = useState(false);
    const [fileUploadText, setFileUploadText] = useState("");
    const [isSigned, setIsSigned] = useState(false);
    const [date, setDate] = useState("");
    const [error, setError] = useState(null);
    const sigPad = useRef(null);
    const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
    const [fileTypes, setFileTypes] = useState("");
    const [profileData, setProfileData] = useState(null);
    const [taxTypeOptions, setTaxTypeOptions] = useState([]);
    const [exemptionCodeOptions, setExemptionCodeOptions] = useState([]);
    const [exemptionCodeFieldName, setExemptionCodeFieldName] = useState("");
    const [exemptionAmountFieldName, setExemptionAmountFieldName] = useState("");
    const [exemptionTaxTypeFieldName, setExemptionTaxTypeFieldName] = useState("");
    const [sectionGroups, setSectionGroups] = useState([{
        id: 1,
        exemptionGroups: [1],
        splitpayment: { fields: [], title: "Split Payment Information", signature: null, signatureText: "", fileUpload: null, fileUploadText: "", fileTypes: "" },
        exemption: { fields: [], title: "Exemption Information", signature: null, signatureText: "", fileUpload: null, fileUploadText: "", fileTypes: "" },
        tobaccodetails: { fields: [], title: "Tobacco Details", signature: null, signatureText: "", fileUpload: null, fileUploadText: "", fileTypes: "" }
    }]);
    const [taxPayerName, setTaxPayerName] = useState("");

    const isEnroll = parseData?.activeComponent === "Enrollment Application";

    // State for tax calculations
    const [taxRates, setTaxRates] = useState({});
    const [splitPaymentFieldNames, setSplitPaymentFieldNames] = useState({
        'Gross Sales Reported': '',
        'Exempt / Excluded Reported': '',
        'Tax Type': '',
        'Net Taxable Reported': '',
        'Reported Tax Amount Due': ''
    });

    // New state for confirmation
    const [showConfirmation, setShowConfirmation] = useState(false);

    // New state for download popup
    const [showDownloadPopup, setShowDownloadPopup] = useState(false);

    // New state to capture data before reset
    const [submittedData, setSubmittedData] = useState(null);

    useEffect(() => {
        const selectedTaxPayer = localStorage.getItem("selectedBusinessName");
        if (selectedTaxPayer) {
            setTaxPayerName(selectedTaxPayer);
        }

        // Load Tribal License Number from localStorage
        const loadTribalLicenseNumber = () => {
            const tribalLicenseNumber = localStorage.getItem("tribalLicenseNumber");
            if (tribalLicenseNumber) {
                setFormData(prev => ({
                    ...prev,
                    tribalLicenseNo: tribalLicenseNumber
                }));
            }
        };

        // Load initially
        loadTribalLicenseNumber();

        // Listen for localStorage changes (when business is selected in other components)
        const handleLocalStorageChange = () => {
            loadTribalLicenseNumber();
        };

        window.addEventListener('localStorageChange', handleLocalStorageChange);

        return () => {
            window.removeEventListener('localStorageChange', handleLocalStorageChange);
        };
    }, []);

    // Dynamic tax rates fetched from API
    const [availableTaxTypes, setAvailableTaxTypes] = useState([]);
    const [displayTaxRates, setDisplayTaxRates] = useState({});

    useEffect(() => {
        const fetchTaxRates = async () => {
            try {
                const response = await fetch(CRITUTILS, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                        "Accept": "application/json"
                    },
                    body: new URLSearchParams({ action: "taxrates" })
                });

                const text = await response.text();
                const cleanedText = text.replace(/[\x00-\x1F\x7F-\x9F]/g, "");

                let data;
                try {
                    data = JSON.parse(cleanedText);
                } catch (parseError) {
                    console.error("Failed to parse tax rates:", parseError);
                    console.error("Cleaned text that failed to parse:", cleanedText);
                    throw new Error("Invalid tax rates response format.");
                }

                if (response.ok && data.success && Array.isArray(data.data)) {
                    const ratesMap = {};
                    const displayMap = {};
                    const types = [];
                    data.data.forEach(item => {
                        const name = item.taxtype;
                        const pct = parseFloat(item.taxrate);
                        if (name && !isNaN(pct)) {
                            // Use decimal for calculations
                            ratesMap[name] = pct / 100;
                            // Keep percentage string for display in PDF
                            displayMap[name] = `${pct.toFixed(2)}%`;
                            types.push(name);
                        }
                    });
                    setTaxRates(ratesMap);
                    setDisplayTaxRates(displayMap);
                    setAvailableTaxTypes(types);
                } else {
                    console.error("Invalid response structure:", {
                        responseOk: response.ok,
                        dataSuccess: data?.success,
                        isArray: Array.isArray(data?.data),
                        data: data
                    });
                    throw new Error(data.message || "Failed to fetch tax rates.");
                }
            } catch (error) {
                console.error("Error fetching tax rates:", error);
                console.error("Error stack:", error.stack);
            }
        };

        fetchTaxRates();
    }, []);

    // Initialize field names when sectionGroups are set
    useEffect(() => {
        if (sectionGroups.length > 0 && sectionGroups[0].splitpayment.fields.length > 0) {
            const splitFields = sectionGroups[0].splitpayment.fields;
            const fieldNameMap = {};

            [
                'Gross Sales Reported',
                'Exempt / Excluded Reported',
                'Tax Type',
                'Net Taxable Reported',
                'Reported Tax Amount Due'
            ].forEach(displayName => {
                const field = splitFields.find(f => f.displayname === displayName);
                if (field) fieldNameMap[displayName] = field.fieldname;
            });

            setSplitPaymentFieldNames(fieldNameMap);
        }
    }, [sectionGroups]);

    // Calculate Exempt / Excluded Reported as sum of all Exemption Amounts for each group
    useEffect(() => {
        if (!exemptionAmountFieldName) return;

        sectionGroups.forEach(group => {
            const exemptSalesKey = `${splitPaymentFieldNames['Exempt / Excluded Reported']}_${group.id}`;

            // Sum all exemption amounts for this group
            let totalExempt = 0;
            group.exemptionGroups.forEach(exemptionId => {
                const exemptionAmountKey = `${exemptionAmountFieldName}_${group.id}_${exemptionId}`;
                const exemptionAmount = parseFloat(formData[exemptionAmountKey] || 0);
                if (!isNaN(exemptionAmount)) {
                    totalExempt += exemptionAmount;
                }
            });

            // Update the Exempt / Excluded Reported field
            setFormData(prev => {
                if (prev[exemptSalesKey] !== totalExempt.toFixed(2)) {
                    return {
                        ...prev,
                        [exemptSalesKey]: totalExempt.toFixed(2)
                    };
                }
                return prev;
            });
        });
    }, [
        formData,
        sectionGroups,
        exemptionAmountFieldName,
        splitPaymentFieldNames
    ]);

    // Handle tax calculations when relevant fields change
    useEffect(() => {
        sectionGroups.forEach(group => {
            const taxTypeKey = `${splitPaymentFieldNames['Tax Type']}_${group.id}`;
            const grossAmountKey = `${splitPaymentFieldNames['Gross Sales Reported']}_${group.id}`;
            const exemptSalesKey = `${splitPaymentFieldNames['Exempt / Excluded Reported']}_${group.id}`;
            const taxableSalesKey = `${splitPaymentFieldNames['Net Taxable Reported']}_${group.id}`;
            const taxDueKey = `${splitPaymentFieldNames['Reported Tax Amount Due']}_${group.id}`;

            const taxType = formData[taxTypeKey];
            const grossAmount = parseFloat(formData[grossAmountKey] || 0);
            const exemptSales = parseFloat(formData[exemptSalesKey] || 0);

            // Only calculate if we have all necessary values
            if (taxType && !isNaN(grossAmount)) {
                const taxRate = taxRates[taxType] || 0;
                const taxableSales = grossAmount - exemptSales;
                let taxDue = taxableSales * taxRate;

                // Check if this is a Tobacco Products Tax and if Tobacco Details has a "Tax Due" field
                const isTobaccoTax = taxTypeOptions.find(opt => opt.sval === taxType && opt.dval === "Tobacco Products Tax");
                if (isTobaccoTax && group.tobaccodetails?.fields?.length > 0) {
                    // Find the "Tax Due" field in Tobacco Details
                    const tobaccoTaxDueField = group.tobaccodetails.fields.find(
                        field => field.displayname === "Tax Due"
                    );
                    
                    if (tobaccoTaxDueField) {
                        const tobaccoTaxDueKey = `${tobaccoTaxDueField.fieldname}_${group.id}`;
                        const tobaccoTaxDue = parseFloat(formData[tobaccoTaxDueKey] || 0);
                        if (!isNaN(tobaccoTaxDue)) {
                            // Add Tobacco Details Tax Due to the calculated tax due
                            taxDue += tobaccoTaxDue;
                        }
                    }
                }

                setFormData(prev => ({
                    ...prev,
                    [taxableSalesKey]: taxableSales.toFixed(2),
                    [taxDueKey]: taxDue.toFixed(2)
                }));
            }
        });
    }, [
        formData,
        sectionGroups,
        splitPaymentFieldNames,
        taxRates,
        taxTypeOptions
    ]);

    // Calculate total tax due (Line 8) - sum of all Reported Tax Amount Due from all groups
    useEffect(() => {
        if (!splitPaymentFieldNames['Reported Tax Amount Due']) return;

        let totalTaxDue = 0;
        sectionGroups.forEach(group => {
            const taxDueKey = `${splitPaymentFieldNames['Reported Tax Amount Due']}_${group.id}`;
            const taxDue = parseFloat(formData[taxDueKey] || 0);
            if (!isNaN(taxDue)) {
                totalTaxDue += taxDue;
            }
        });

        const newTotal = totalTaxDue.toFixed(2);
        setFormData(prev => {
            // Only update if the value actually changed
            if (prev.totalTaxDue !== newTotal) {
                return {
                    ...prev,
                    totalTaxDue: newTotal,
                    // Set paymentAmount to match totalTaxDue (but keep it editable)
                    paymentAmount: newTotal
                };
            }
            return prev;
        });
    }, [
        formData,
        sectionGroups,
        splitPaymentFieldNames
    ]);

    useEffect(() => {
        const fetchFormFields = async (itype, stype) => {
            setIsLoading(true);
            try {
                const response = await fetch(UTILS, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                        "Accept": "application/json"
                    },
                    body: new URLSearchParams({
                        action: "desc",
                        itype: itype,
                        stype: stype,
                    }),
                });

                const text = await response.text();
                const cleanedText = text.replace(/[\x00-\x1F\x7F-\x9F]/g, "");

                let data;
                try {
                    data = JSON.parse(cleanedText);
                  

                } catch (parseError) {
                    console.error("Failed to parse form fields:", parseError);
                    throw new Error("Invalid server response format.");
                }

                if (response.ok) {
                    return data;
                } else {
                    throw new Error(data.message || "Failed to fetch form fields.");
                }
            } catch (error) {
                console.error("Error loading form:", error);
                setError(error.message);
                return null;
            }
        };

        const fetchDropDownFields = async (itemValueGroup) => {
            if (picklistCache[itemValueGroup]?.data) return;

            setPicklistCache((prev) => ({
                ...prev,
                [itemValueGroup]: { ...prev[itemValueGroup], isLoading: true },
            }));

            try {
                const response = await fetch(UTILS, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                        "Accept": "application/json"
                    },
                    body: new URLSearchParams({ action: "idv", s: itemValueGroup || "YesNo" }),
                });

                const text = await response.text();
                const cleanedText = text.replace(/[\x00-\x1F\x7F-\x9F]/g, "");
                let data;
                try {
                    data = JSON.parse(cleanedText);
                } catch (parseError) {
                    console.error("Failed to parse dropdown data:", parseError);
                    console.error("Original text:", text);
                    throw new Error("Invalid dropdown data format.");
                }

                setPicklistCache((prev) => ({
                    ...prev,
                    [itemValueGroup]: { data, isLoading: false },
                }));
            } catch (error) {
                console.error("Error fetching dropdown:", error);
                setPicklistCache((prev) => ({
                    ...prev,
                    [itemValueGroup]: { isLoading: false, error: error.message },
                }));
            }
        };

        const fetchAndCombineData = async () => {
            try {
                const taxRevenueData = await fetchFormFields("funding", "taxrevenue");
                const splitPaymentData = await fetchFormFields("funding", "splitpayment");
                const fundingExcemption = await fetchFormFields("funding", "exemption");
                const tobaccoDetailsData = await fetchFormFields("funding", "tobaccodetails");

                if (!taxRevenueData || !splitPaymentData || !fundingExcemption || !tobaccoDetailsData) return;

                // Store exemption code field name
                const exemptionCodeField = fundingExcemption.Fields?.find(f => f.displayname === "Exemption Code");
                if (exemptionCodeField) {
                    setExemptionCodeFieldName(exemptionCodeField.fieldname);
                }

                // Store exemption amount field name
                const exemptionAmountField = fundingExcemption.Fields?.find(f => f.displayname === "Exemption Amount");
                if (exemptionAmountField) {
                    setExemptionAmountFieldName(exemptionAmountField.fieldname);
                }

                // Store tax type field name for exemption section
                const exemptionTaxTypeField = fundingExcemption.Fields?.find(f => f.displayname === "Tax Type");
                if (exemptionTaxTypeField) {
                    setExemptionTaxTypeFieldName(exemptionTaxTypeField.fieldname);
                }

                setFormSections(prev => ({
                    taxrevenue: {
                        ...prev.taxrevenue,
                        fields: taxRevenueData.Fields || [],
                        signature: taxRevenueData.Signature,
                        signatureText: taxRevenueData.SignatureText || "",
                        fileUpload: taxRevenueData.FileUpload,
                        fileUploadText: taxRevenueData.FileUploadText || "",
                        fileTypes: taxRevenueData.FileTypes || ""
                    }
                }));

                setSectionGroups([{
                    id: 1,
                    exemptionGroups: [1],
                    splitpayment: {
                        fields: splitPaymentData.Fields || [],
                        signature: splitPaymentData.Signature,
                        signatureText: splitPaymentData.SignatureText || "",
                        fileUpload: splitPaymentData.FileUpload,
                        fileUploadText: splitPaymentData.FileUploadText || "",
                        fileTypes: splitPaymentData.FileTypes || ""
                    },
                    exemption: {
                        fields: fundingExcemption.Fields || [],
                        signature: fundingExcemption.Signature,
                        signatureText: fundingExcemption.SignatureText || "",
                        fileUpload: fundingExcemption.FileUpload,
                        fileUploadText: fundingExcemption.FileUploadText || "",
                        fileTypes: fundingExcemption.FileTypes || ""
                    },
                    tobaccodetails: {
                        fields: tobaccoDetailsData.Fields || [],
                        signature: tobaccoDetailsData.Signature,
                        signatureText: tobaccoDetailsData.SignatureText || "",
                        fileUpload: tobaccoDetailsData.FileUpload,
                        fileUploadText: tobaccoDetailsData.FileUploadText || "",
                        fileTypes: tobaccoDetailsData.FileTypes || ""
                    }
                }]);

                const allFields = [
                    ...(taxRevenueData.Fields || []),
                    ...(splitPaymentData.Fields || []),
                    ...(fundingExcemption.Fields || []),
                    ...(tobaccoDetailsData.Fields || [])
                ];

                const initialFormData = {};
                allFields.forEach((field) => {
                    if (field.defaultvalue) {
                        initialFormData[field.fieldname] = field.defaultvalue;
                    } else if (field.displayname === "Month") {
                        initialFormData[field.fieldname] = defaultDate.month;
                    } else if (field.displayname === "Year") {
                        initialFormData[field.fieldname] = defaultDate.year;
                    }
                });

                // Initialize group-specific fields for the first section group
                if (exemptionCodeFieldName) {
                    initialFormData[`${exemptionCodeFieldName}_1_1`] = "";
                }
                if (exemptionAmountFieldName) {
                    initialFormData[`${exemptionAmountFieldName}_1_1`] = "";
                }
                if (exemptionTaxTypeFieldName) {
                    initialFormData[`${exemptionTaxTypeFieldName}_1_1`] = "";
                }

                // Preserve tribalLicenseNo, reportingPeriodMonth, and reportingPeriodYear from initial state
                setFormData(prev => ({
                    ...initialFormData,
                    tribalLicenseNo: prev.tribalLicenseNo || '',
                    reportingPeriodMonth: prev.reportingPeriodMonth || defaultDate.month,
                    reportingPeriodYear: prev.reportingPeriodYear || defaultDate.year
                }));

                const hasSignature = taxRevenueData.Signature || splitPaymentData.Signature || fundingExcemption.Signature || tobaccoDetailsData.Signature;
                const hasFileUpload = taxRevenueData.FileUpload || splitPaymentData.FileUpload || fundingExcemption.FileUpload || tobaccoDetailsData.FileUpload;

                setShowSignature(hasSignature);
                setShowFileUpload(hasFileUpload);

                const picklistFields = allFields.filter(
                    (field) => field.fieldtype === "picklist"
                );
                await Promise.all(
                    picklistFields.map(async (field) => {
                        await fetchDropDownFields(field.itemvaluegroup);
                    })
                );
            } catch (error) {
                console.error("Error in fetchAndCombineData:", error);
                setError(error.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAndCombineData();
    }, []);

    useEffect(() => {
        const fetchTaxTypes = async () => {
            try {
                const response = await fetch(UTILS, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                        "Accept": "application/json"
                    },
                    body: new URLSearchParams({
                        action: "sqlo",
                        itype: "funding",
                        stype: "splitpayment",
                        fldn: "flexfield31"
                    }),
                });

                const text = await response.text();
                const cleanedText = text.replace(/[\x00-\x1F\x7F-\x9F]/g, "");

                let data;
                try {
                    data = JSON.parse(cleanedText);
                } catch (parseError) {
                    console.error("Failed to parse tax type data:", parseError);
                    throw new Error("Invalid tax type data format.");
                }

                if (response.ok) {
                    setTaxTypeOptions(data);
                } else {
                    throw new Error(data.message || "Failed to fetch tax types.");
                }
            } catch (error) {
                console.error("Error fetching tax types:", error);
            }
        };
        fetchTaxTypes();

    }, []);

    const fetchExemptionCodes = async (taxType, groupId, exemptionId) => {
        try {
            const response = await fetch(UTILS, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    "Accept": "application/json"
                },
                body: new URLSearchParams({
                    action: "exempcds",
                    itype: "funding",
                    stype: "exemption",
                    fldn: "flexfield25",
                    hiddendefaults: taxType
                }),
            });

            const text = await response.text();
            const cleanedText = text.replace(/[\x00-\x1F\x7F-\x9F]/g, "");
            let data;
            try {
                data = JSON.parse(cleanedText);
            } catch (parseError) {
                console.error("Failed to parse exemption code data:", parseError);
                throw new Error("Invalid exemption code data format.");
            }

            if (response.ok) {
                // Update state to store exemption codes by group and exemption ID
                setExemptionCodeOptions(prev => ({
                    ...prev,
                    [`${groupId}_${exemptionId}`]: data
                }));
            } else {
                throw new Error(data.message || "Failed to fetch exemption codes.");
            }
        } catch (error) {
            console.error("Error fetching exemption codes:", error);
        }
    };

    const handleAddExemptionGroup = (groupId) => {
        setSectionGroups(prevGroups => {
            return prevGroups.map(group => {
                if (group.id === groupId) {
                    const newExemptionId = group.exemptionGroups.length > 0
                        ? Math.max(...group.exemptionGroups) + 1
                        : 1;

                    return {
                        ...group,
                        exemptionGroups: [...group.exemptionGroups, newExemptionId]
                    };
                }
                return group;
            });
        });

        setFormData(prev => {
            const group = sectionGroups.find(g => g.id === groupId);
            const newExemptionId = group && group.exemptionGroups.length > 0
                ? Math.max(...group.exemptionGroups) + 1
                : 1;

            const newData = { ...prev };
            if (exemptionCodeFieldName) {
                newData[`${exemptionCodeFieldName}_${groupId}_${newExemptionId}`] = "";
            }
            if (exemptionAmountFieldName) {
                newData[`${exemptionAmountFieldName}_${groupId}_${newExemptionId}`] = "";
            }
            if (exemptionTaxTypeFieldName) {
                newData[`${exemptionTaxTypeFieldName}_${groupId}_${newExemptionId}`] = "";
            }
            return newData;
        });
    };

    const handleRemoveExemptionGroup = (groupId, exemptionId) => {
        setSectionGroups(prevGroups => {
            return prevGroups.map(group => {
                if (group.id === groupId) {
                    if (group.exemptionGroups.length > 1) {
                        return {
                            ...group,
                            exemptionGroups: group.exemptionGroups.filter(id => id !== exemptionId)
                        };
                    }
                }
                return group;
            });
        });

        setFormData(prev => {
            const newData = { ...prev };
            if (exemptionCodeFieldName) {
                delete newData[`${exemptionCodeFieldName}_${groupId}_${exemptionId}`];
            }
            if (exemptionAmountFieldName) {
                delete newData[`${exemptionAmountFieldName}_${groupId}_${exemptionId}`];
            }
            if (exemptionTaxTypeFieldName) {
                delete newData[`${exemptionTaxTypeFieldName}_${groupId}_${exemptionId}`];
            }
            return newData;
        });
    };

    const handleAddSectionGroup = () => {
        const newGroupId = sectionGroups.length > 0
            ? Math.max(...sectionGroups.map(g => g.id)) + 1
            : 1;

        setSectionGroups(prev => [
            ...prev,
            {
                id: newGroupId,
                exemptionGroups: [1],
                splitpayment: { ...prev[0].splitpayment },
                exemption: { ...prev[0].exemption },
                tobaccodetails: { ...prev[0].tobaccodetails }
            }
        ]);

        // Initialize form data for the new group
        const initialData = {};
        [...sectionGroups[0].splitpayment.fields, ...sectionGroups[0].exemption.fields, ...sectionGroups[0].tobaccodetails.fields].forEach(field => {
            if (field.defaultvalue) {
                initialData[`${field.fieldname}_${newGroupId}`] = field.defaultvalue;
            }
        });

        setFormData(prev => ({
            ...prev,
            ...initialData,
            ...(exemptionCodeFieldName && { [`${exemptionCodeFieldName}_${newGroupId}_1`]: "" }),
            ...(exemptionAmountFieldName && { [`${exemptionAmountFieldName}_${newGroupId}_1`]: "" }),
            ...(exemptionTaxTypeFieldName && { [`${exemptionTaxTypeFieldName}_${newGroupId}_1`]: "" })
        }));
    };

    const handleRemoveSectionGroup = (groupId) => {
        if (sectionGroups.length > 1) {
            setSectionGroups(prev => prev.filter(group => group.id !== groupId));

            // Remove all fields related to this group
            setFormData(prev => {
                const newData = { ...prev };
                Object.keys(prev).forEach(key => {
                    if (key.endsWith(`_${groupId}`) ||
                        key.startsWith(`${exemptionCodeFieldName}_${groupId}_`) ||
                        key.startsWith(`${exemptionAmountFieldName}_${groupId}_`) ||
                        key.startsWith(`${exemptionTaxTypeFieldName}_${groupId}_`)) {
                        delete newData[key];
                    }
                });
                return newData;
            });
        }
    };

    const handleHomeClick = () => {
        navigate("/home");
        if (onItemSelect) onItemSelect();
    };

    const prepareRequestData = () => {
        const requestData = {
            uid: "123213",
            taxInfo: {},
            splitPayment: []
        };

        // 1. Add tax revenue information
        formSections.taxrevenue.fields.forEach(field => {
            if (formData[field.fieldname]) {
                requestData.taxInfo[field.fieldname] = formData[field.fieldname];
            }
        });
        const taxPayer = localStorage.getItem("selectedTaxPayer");
        requestData.taxInfo["flexfield17"] = taxPayer;

        // 2. Add split payment and exemption information
        sectionGroups.forEach(group => {
            const splitPaymentGroup = {
                exemptions: [],
                tobaccoDetails: null
            };

            // Add split payment fields
            group.splitpayment.fields.forEach(field => {
                const fieldKey = `${field.fieldname}_${group.id}`;
                if (formData[fieldKey]) {
                    splitPaymentGroup[field.fieldname] = formData[fieldKey];
                }
            });

            // Check if this is a Tobacco Products Tax
            const taxTypeFieldName = splitPaymentFieldNames['Tax Type'];
            const taxTypeKey = `${taxTypeFieldName}_${group.id}`;
            const selectedTaxType = formData[taxTypeKey];
            const isTobaccoTax = taxTypeOptions.find(opt => opt.sval === selectedTaxType && opt.dval === "Tobacco Products Tax");

            // Add tobacco details if it's a tobacco tax type
            if (isTobaccoTax && group.tobaccodetails.fields.length > 0) {
                const tobaccoDetailsData = {};
                group.tobaccodetails.fields.forEach(field => {
                    const fieldKey = `${field.fieldname}_${group.id}`;
                    if (formData[fieldKey]) {
                        tobaccoDetailsData[field.fieldname] = formData[fieldKey];
                    }
                });
                if (Object.keys(tobaccoDetailsData).length > 0) {
                    splitPaymentGroup.tobaccoDetails = tobaccoDetailsData;
                }
            }

            // Add exemption groups
            group.exemptionGroups.forEach(exemptionId => {
                const exemptionGroup = {};

                group.exemption.fields.forEach(field => {
                    const fieldKey = `${field.fieldname}_${group.id}_${exemptionId}`;
                    const fieldValue = formData[fieldKey];

                    // Handle empty exemption amount - send 0 instead of empty string
                    if (field.displayname === "Exemption Amount") {
                        exemptionGroup[field.fieldname] = fieldValue === "" || fieldValue === null || fieldValue === undefined ? "0" : fieldValue;
                    } else if (fieldValue) {
                        exemptionGroup[field.fieldname] = fieldValue;
                    }
                });

                if (Object.keys(exemptionGroup).length > 0) {
                    splitPaymentGroup.exemptions.push(exemptionGroup);
                }
            });

            if (Object.keys(splitPaymentGroup).length > 1 || splitPaymentGroup.exemptions.length > 0) {
                requestData.splitPayment.push(splitPaymentGroup);
            }
        });

        return requestData;
    };

    const generatePDF = async (pdfFormData, pdfSectionGroups, pdfTaxPayerName, signatureDataURL = null) => {
        // Use US Letter size (8.5 x 11 inches)
        const doc = new jsPDF({ unit: "pt", format: "letter" });
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        // Get business information from localStorage
        const businessAddress = localStorage.getItem("selectedBusinessAddress") || "";
        const businessCity = localStorage.getItem("selectedBusinessCity") || "";
        const businessState = localStorage.getItem("selectedBusinessState") || "";
        const businessZip = localStorage.getItem("selectedBusinessZip") || "";

        // Get Tribal License No. and Reporting Period from form data (available to all functions)
        // Use default values if not provided
        const tribalLicenseNo = pdfFormData?.tribalLicenseNo || "";
        const reportingMonth = pdfFormData?.reportingPeriodMonth || defaultDate.month;
        const reportingYear = pdfFormData?.reportingPeriodYear || defaultDate.year;
        const reportingPeriod = reportingMonth && reportingYear ? `${reportingMonth} ${reportingYear}` : `${defaultDate.month} ${defaultDate.year}`;

        // Helper function to load image as base64
        const loadImageAsBase64 = (imagePath) => {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                
                img.onload = () => {
                    try {
                        const canvas = document.createElement('canvas');
                        canvas.width = img.width;
                        canvas.height = img.height;
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(img, 0, 0);
                        const dataURL = canvas.toDataURL('image/png');
                        resolve(dataURL);
                    } catch (error) {
                        reject(error);
                    }
                };
                
                img.onerror = () => {
                    console.warn(`Failed to load image from path: ${imagePath}`);
                    reject(new Error(`Failed to load image: ${imagePath}`));
                };
                
                // Use the imported webpack path
                img.src = imagePath;
            });
        };

        // Helper function to get values from form data
        const getTaxRevenueValueByDisplayName = (name) => {
            const fields = (formSections?.taxrevenue?.fields) || [];
            const found = fields.find(f => f.displayname === name);
            if (!found) return "";
            return pdfFormData?.[found.fieldname] ?? "";
        };

        const getGroupValueByDisplay = (group, displayIncludes) => {
            const fields = (group?.splitpayment?.fields) || [];
            const f = fields.find(ff => (ff.displayname || "").toLowerCase().includes(displayIncludes));
            if (!f) return "";
            return pdfFormData?.[`${f.fieldname}_${group.id}`] ?? "";
        };

        // Function to get exemption data for Schedule A
        const getExemptionDataForScheduleA = () => {
            const exemptionData = {
                "Sales Tax": [],
                "Sales Tax (Excess of $2500 on Single Item Sale)": [],
                "Restaurant Privilege": [],
                "Transient Lodging Tax": [],
                "Utility Business Activity Tax": [],
                "Liquor Privilege Tax": [],
                "Tobacco Products Tax": []
            };

            // Map tax types to their Schedule A sections - FIXED MAPPING
            const taxTypeToSection = {
                "Sales Tax": "Sales Tax",
                "Sales Tax (Excess of $2500 on Single Item Sale)": "Sales Tax (Excess of $2500 on Single Item Sale)",
                "Restaurant Privilege": "Restaurant Privilege",
                "Transient Lodging Tax": "Transient Lodging Tax",
                "Utility Business Activity Tax": "Utility Business Activity Tax",
                "Liquor Privilege Tax": "Liquor Privilege Tax",
                "Tobacco Products Tax": "Tobacco Products Tax"
            };

            // Collect exemption data from all section groups
            pdfSectionGroups.forEach(group => {
                const taxType = getGroupValueByDisplay(group, "tax type");
                if (!taxType) return;

                group.exemptionGroups.forEach(exemptionId => {
                    const exemptionCode = pdfFormData[`${exemptionCodeFieldName}_${group.id}_${exemptionId}`];
                    const exemptionAmount = pdfFormData[`${exemptionAmountFieldName}_${group.id}_${exemptionId}`];

                    if (exemptionCode && exemptionAmount && parseFloat(exemptionAmount) > 0) {
                        const section = taxTypeToSection[taxType];
                        if (section && exemptionData[section]) {
                            exemptionData[section].push({
                                code: exemptionCode.split(' - ')[0], // Extract just the code part (e.g., "S01")
                                amount: parseFloat(exemptionAmount)
                            });
                        }
                    }
                });
            });

            return exemptionData;
        };

        // Function to aggregate data by tax type while keeping all tax types
        const aggregateDataByTaxType = (sectionGroups) => {
            const aggregatedData = {};

            // Initialize from dynamic tax types; fall back to any seen types in groups
            const initTypes = availableTaxTypes.length > 0 ? availableTaxTypes : [];
            initTypes.forEach(taxType => {
                aggregatedData[taxType] = {
                    taxType,
                    grossSales: 0,
                    exemptAmount: 0,
                    netTaxable: 0,
                    taxDue: 0,
                    description: taxType,
                    rate: displayTaxRates[taxType] || `${((taxRates[taxType] || 0) * 100).toFixed(2)}%`
                };
            });

            // Now aggregate data from section groups
            sectionGroups.forEach(group => {
                const taxType = getGroupValueByDisplay(group, "tax type");
                const grossSales = parseFloat(getGroupValueByDisplay(group, "gross") || 0);
                const exemptAmount = parseFloat(getGroupValueByDisplay(group, "exempt") || 0);
                const netTaxable = parseFloat(getGroupValueByDisplay(group, "net") || 0);
                const taxDue = parseFloat(getGroupValueByDisplay(group, "due") || 0);

                // Ensure tax type exists even if not initialized
                if (taxType) {
                    if (!aggregatedData[taxType]) {
                        aggregatedData[taxType] = {
                            taxType,
                            grossSales: 0,
                            exemptAmount: 0,
                            netTaxable: 0,
                            taxDue: 0,
                            description: taxType,
                            rate: displayTaxRates[taxType] || `${((taxRates[taxType] || 0) * 100).toFixed(2)}%`
                        };
                    }
                    aggregatedData[taxType].grossSales += grossSales;
                    aggregatedData[taxType].exemptAmount += exemptAmount;
                    aggregatedData[taxType].netTaxable += netTaxable;
                    aggregatedData[taxType].taxDue += taxDue;
                }
            });

            return Object.values(aggregatedData);
        };

        // Get aggregated data (this will include all tax types, even with zero values)
        const aggregatedData = aggregateDataByTaxType(pdfSectionGroups);

        // Get exemption data for Schedule A
        const exemptionData = getExemptionDataForScheduleA();

        // PAGE 1 - FORM TR-1 TAX RETURN with centered header text
        const drawPage1 = async () => {
            // Load logo image - use public folder path for production reliability
            let logoDataURL = null;
            try {
                // Try to load from public folder first (works in both dev and production)
                const publicPath = `${window.location.origin}/critLogo.png`;
                logoDataURL = await loadImageAsBase64(publicPath);
            } catch (publicError) {
                console.warn('Failed to load logo from public folder, trying webpack import');
                try {
                    // Fallback to webpack imported path
                    logoDataURL = await loadImageAsBase64(critLogo);
                } catch (importError) {
                    console.error('Failed to load logo image from both sources:', publicError, importError);
                }
            }

            // Header section with logo on the left
            const logoWidth = 70; // Width of logo area
            const logoHeight = 70; // Height of logo area
            const logoX = 40; // X position of logo
            const logoY = 30; // Y position of logo
            const textStartX = logoX + logoWidth + 20; // X position where text starts (after logo + spacing)

            // Add logo image on the left side if available
            if (logoDataURL) {
                try {
                    doc.addImage(logoDataURL, 'PNG', logoX, logoY, logoWidth, logoHeight);
                } catch (error) {
                    console.error('Error adding logo to PDF:', error);
                }
            }

            // Header text on the right side of logo
            doc.setFont("helvetica", "bold");
            doc.setFontSize(16);
            doc.text("FORM TR-1 TAX RETURN", textStartX, 40);

            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            doc.text("Colorado River Indian Tribes", textStartX, 55);
            doc.text("26600 Mohave Road", textStartX, 67);
            doc.text("Parker, Arizona 85344", textStartX, 79);
            doc.text("Ph.: (928) 575-1532", textStartX, 91);
            doc.text("Email: taxinfo@crit-nsn.gov", textStartX, 103);

            // Right side - Reporting period box (increased height)
            doc.setDrawColor(0);
            doc.setLineWidth(0.5);
            doc.rect(pageWidth - 200, 30, 170, 110);

            doc.setFont("helvetica", "bold");
            doc.setFontSize(6);
            doc.text("THIS RETURN IS DUE THE LAST DAY OF THE", pageWidth - 195, 50);
            doc.text("MONTH FOLLOWING THE REPORTING PERIOD", pageWidth - 195, 60);

            // Inner box for Tribal License No. (increased height)
            doc.rect(pageWidth - 195, 65, 160, 30);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(6);
            doc.text("TRIBAL LICENSE NO.", pageWidth - 190, 75);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            if (tribalLicenseNo) {
                const licenseLines = doc.splitTextToSize(tribalLicenseNo, 150);
                doc.text(licenseLines, pageWidth - 190, 85);
            } else {
                doc.setFontSize(6);
                doc.text("(enter below)", pageWidth - 190, 85);
            }

            // Inner box for Reporting Period (increased height)
            doc.rect(pageWidth - 195, 97, 160, 30);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(6);
            doc.text("REPORTING PERIOD", pageWidth - 190, 107);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            if (reportingPeriod) {
                doc.text(reportingPeriod, pageWidth - 190, 117);
            } else {
                doc.setFontSize(6);
                doc.text("(enter mo/yr below)", pageWidth - 190, 117);
            }

            // Taxpayer Information Section (moved down to avoid overlap with Reporting Period box)
            doc.setFont("helvetica", "bold");
            doc.setFontSize(11);
            doc.text("I. TAXPAYER INFORMATION", 40, 150);

            doc.setLineWidth(0.8);
            doc.rect(40, 155, pageWidth - 80, 80);

            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);

            // BUSINESS AND/OR OWNER NAME
            doc.text("BUSINESS AND/OR OWNER NAME:", 45, 172);
            doc.line(210, 177, pageWidth - 60, 177);
            if (pdfTaxPayerName) {
                const nameLines = doc.splitTextToSize(pdfTaxPayerName, pageWidth - 250);
                doc.text(nameLines, 215, 172);
            }

            // ADDRESS
            doc.text("ADDRESS:", 45, 192);
            doc.line(100, 197, pageWidth - 60, 197);
            if (businessAddress) {
                const addressLines = doc.splitTextToSize(businessAddress, pageWidth - 250);
                doc.text(addressLines, 105, 192);
            }

            // CITY, STATE, ZIP CODE
            doc.text("CITY", 45, 212);
            doc.line(75, 217, 200, 217);
            if (businessCity) {
                doc.text(businessCity, 80, 212);
            }

            doc.text("STATE", 210, 212);
            doc.line(240, 217, 320, 217);
            if (businessState) {
                doc.text(businessState, 245, 212);
            }

            doc.text("ZIP CODE", 330, 212);
            doc.line(380, 217, 450, 217);
            if (businessZip) {
                doc.text(businessZip, 385, 212);
            }

            // Transaction Detail Section
            doc.setFont("helvetica", "bold");
            doc.setFontSize(11);
            doc.text("II. TRANSACTION DETAIL", 40, 255);

            // Column headers - INCREASED HEIGHT with CENTERED TEXT
            const tableTop = 265;
            const headerHeight = 30; // Increased from 20 to 30
            const colWidths = [20, 140, 80, 85, 80, 50, 80]; // Line#, Business Desc, Col1, Col2, Col3, Col4, Col5

            // Draw main table border with increased height
            doc.setLineWidth(0.8);
            doc.rect(40, tableTop, pageWidth - 80, headerHeight);

            // Draw vertical lines for headers
            let xPos = 40;
            doc.setFont("helvetica", "bold");
            doc.setFontSize(6);

            // Line column (small) - CENTERED
            doc.line(xPos + colWidths[0], tableTop, xPos + colWidths[0], tableTop + headerHeight);
            const lineCenterX = xPos + (colWidths[0] / 2);
            doc.text("Line", lineCenterX, tableTop + 12, { align: 'center' });

            // Business Description column - CENTERED
            xPos += colWidths[0];
            doc.line(xPos + colWidths[1], tableTop, xPos + colWidths[1], tableTop + headerHeight);
            const businessDescCenterX = xPos + (colWidths[1] / 2);
            doc.text("BUSINESS DESCRIPTION", businessDescCenterX, tableTop + 12, { align: 'center' });

            // COLUMN 1 - CENTERED
            xPos += colWidths[1];
            doc.line(xPos + colWidths[2], tableTop, xPos + colWidths[2], tableTop + headerHeight);
            const col1CenterX = xPos + (colWidths[2] / 2);
            doc.text("COLUMN 1", col1CenterX, tableTop + 8, { align: 'center' });
            doc.setFontSize(7);
            doc.text("GROSS SALES", col1CenterX, tableTop + 16, { align: 'center' });
            doc.setFontSize(6);
            doc.text("(round to nearest dollar)", col1CenterX, tableTop + 24, { align: 'center' });

            // COLUMN 2 - CENTERED
            xPos += colWidths[2];
            doc.line(xPos + colWidths[3], tableTop, xPos + colWidths[3], tableTop + headerHeight);
            const col2CenterX = xPos + (colWidths[3] / 2);
            doc.setFontSize(6);
            doc.text("COLUMN 2", col2CenterX, tableTop + 8, { align: 'center' });
            doc.setFontSize(7);
            doc.text("- EXEMPT / EXCLUDED", col2CenterX, tableTop + 16, { align: 'center' });
            doc.setFontSize(6);
            doc.text("AMOUNT (From Schedule A)", col2CenterX, tableTop + 24, { align: 'center' });

            // COLUMN 3 - CENTERED
            xPos += colWidths[3];
            doc.line(xPos + colWidths[4], tableTop, xPos + colWidths[4], tableTop + headerHeight);
            const col3CenterX = xPos + (colWidths[4] / 2);
            doc.setFontSize(6);
            doc.text("COLUMN 3", col3CenterX, tableTop + 8, { align: 'center' });
            doc.setFontSize(7);
            doc.text("= NET TAXABLE", col3CenterX, tableTop + 16, { align: 'center' });
            doc.setFontSize(6);
            doc.text("(round to nearest dollar)", col3CenterX, tableTop + 24, { align: 'center' });

            // COLUMN 4 - CENTERED
            xPos += colWidths[4];
            doc.line(xPos + colWidths[5], tableTop, xPos + colWidths[5], tableTop + headerHeight);
            const col4CenterX = xPos + (colWidths[5] / 2);
            doc.setFontSize(6);
            doc.text("COLUMN 4", col4CenterX, tableTop + 8, { align: 'center' });
            doc.setFontSize(7);
            doc.text("X TAX RATE", col4CenterX, tableTop + 16, { align: 'center' });

            // COLUMN 5 - CENTERED
            xPos += colWidths[5];
            const col5CenterX = xPos + (colWidths[6] / 2);
            doc.setFontSize(6);
            doc.text("COLUMN 5", col5CenterX, tableTop + 8, { align: 'center' });
            doc.setFontSize(7);
            doc.text("= TAX DUE", col5CenterX, tableTop + 16, { align: 'center' });
            doc.setFontSize(6);
            doc.text("(round to nearest dollar)", col5CenterX, tableTop + 24, { align: 'center' });

            // Reset font size for data rows
            doc.setFontSize(11);

            // Data rows - adjust starting position to account for taller header
            let currentY = tableTop + headerHeight;

            // Use aggregated data (includes all tax types, even with zero values)
            aggregatedData.forEach((data, index) => {
                const rowHeight = 25;

                // Draw row border
                doc.setDrawColor(0);
                doc.setLineWidth(0.3);
                doc.rect(40, currentY, pageWidth - 80, rowHeight);

                // Line number - CENTERED
                doc.setFont("helvetica", "bold");
                doc.setFontSize(9);
                const lineNumCenterX = 40 + (colWidths[0] / 2);
                doc.text((index + 1).toString(), lineNumCenterX, currentY + 16, { align: 'center' });

                // Business description - LEFT ALIGNED (as it's descriptive text)
                doc.setFont("helvetica", "normal");
                const descLines = doc.splitTextToSize(data.description, colWidths[1] - 15);

                // Calculate vertical position for centered text
                const lineHeight = 9;
                const totalTextHeight = descLines.length * lineHeight;
                const startY = currentY + (rowHeight - totalTextHeight) / 2 + lineHeight;

                doc.text(descLines, 40 + colWidths[0] + 5, startY);

                // Column positions - CENTERED alignment for all data columns
                let colX = 40 + colWidths[0] + colWidths[1]; // Start at COLUMN 1

                // COLUMN 1 - Gross Sales (centered)
                const grossSales = data.grossSales > 0 ? data.grossSales.toFixed(2) : "";
                const col1Center = colX + (colWidths[2] / 2);
                doc.text(grossSales, col1Center, currentY + 16, { align: 'center' });

                // COLUMN 2 - Exempt/Excluded Amount (centered)
                colX += colWidths[2];
                const exemptAmount = data.exemptAmount > 0 ? data.exemptAmount.toFixed(2) : "";
                const col2Center = colX + (colWidths[3] / 2);
                doc.text(exemptAmount, col2Center, currentY + 16, { align: 'center' });

                // COLUMN 3 - Net Taxable (centered)
                colX += colWidths[3];
                const netTaxable = data.netTaxable > 0 ? data.netTaxable.toFixed(2) : "";
                const col3Center = colX + (colWidths[4] / 2);
                doc.text(netTaxable, col3Center, currentY + 16, { align: 'center' });

                // COLUMN 4 - Tax Rate (centered)
                colX += colWidths[4];
                const col4Center = colX + (colWidths[5] / 2);
                doc.text(data.rate, col4Center, currentY + 16, { align: 'center' });

                // COLUMN 5 - Tax Due (centered)
                colX += colWidths[5];
                const taxDue = data.taxDue > 0 ? data.taxDue.toFixed(2) : "";
                const col5Center = colX + (colWidths[6] / 2);
                doc.text(taxDue, col5Center, currentY + 16, { align: 'center' });

                // Draw vertical lines between columns
                xPos = 40;
                for (let i = 0; i < colWidths.length - 1; i++) {
                    xPos += colWidths[i];
                    doc.line(xPos, currentY, xPos, currentY + rowHeight);
                }

                currentY += rowHeight;
            });

            // Summary rows (Lines 8 & 9 from PDF) - WITH MERGED CELLS
            const summaryY = currentY;
            const summaryRows = [
                { text: "Add lines 1 - 7 in Column 5. This is your TOTAL TAX DUE . . .", bold: true },
                { text: "ENTER AMOUNT YOU ARE PAYING WITH THIS RETURN . . .", bold: true }
            ];

            // Calculate total tax due from column 5
            const totalTaxDue = aggregatedData.reduce((sum, data) => sum + data.taxDue, 0);

            summaryRows.forEach((row, index) => {
                const rowY = summaryY + (index * 25);
                const lineNumber = index + 8;

                // Draw row border - full width
                doc.rect(40, rowY, pageWidth - 80, 25);

                // Line number (8 and 9) - CENTERED in first small column
                doc.setFont("helvetica", "bold");
                doc.setFontSize(9);
                const lineNumCenterX = 40 + (colWidths[0] / 2);
                doc.text(lineNumber.toString(), lineNumCenterX, rowY + 16, { align: 'center' });

                // Text - spans from Business Description column through Column 4 (merging 5 cells)
                doc.setFont("helvetica", row.bold ? "bold" : "normal");
                doc.setFontSize(9);

                // Calculate the width of the merged area (from Business Desc through Column 4)
                const mergedWidth = colWidths[1] + colWidths[2] + colWidths[3] + colWidths[4] + colWidths[5];

                // Position text in the merged area (starting from Business Desc column)
                const mergedStartX = 40 + colWidths[0] + 5;

                // For line 8, show the total tax due on the right side in Column 5
                if (index === 0) {
                    // Split the text to fit in the available width
                    const availableWidth = mergedWidth - 10; // Subtract padding
                    const textLines = doc.splitTextToSize(row.text, availableWidth);

                    // Calculate vertical centering for multi-line text
                    const lineHeight = 9;
                    const totalTextHeight = textLines.length * lineHeight;
                    const startY = rowY + (25 - totalTextHeight) / 2 + lineHeight;

                    doc.text(textLines, mergedStartX, startY);

                    // Draw total tax due in Column 5 (right-aligned)
                    const totalX = 40 + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] + colWidths[4] + colWidths[5] + (colWidths[6] / 2);
                    doc.text(totalTaxDue.toFixed(2), totalX, rowY + 16, { align: 'center' });
                } else {
                    // For line 9, just show the text in the merged area
                    const availableWidth = mergedWidth - 10;
                    const textLines = doc.splitTextToSize(row.text, availableWidth);

                    const lineHeight = 9;
                    const totalTextHeight = textLines.length * lineHeight;
                    const startY = rowY + (25 - totalTextHeight) / 2 + lineHeight;

                    doc.text(textLines, mergedStartX, startY);
                }

                // Draw vertical lines - only show the first line (after line number) and the last line (before Column 5)
                // First vertical line (after line number column)
                doc.line(40 + colWidths[0], rowY, 40 + colWidths[0], rowY + 25);

                // Last vertical line (before Column 5) - this creates the merged appearance
                const lastLineX = 40 + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] + colWidths[4] + colWidths[5];
                doc.line(lastLineX, rowY, lastLineX, rowY + 25);

                // Don't draw the intermediate vertical lines to create merged cells
            });

            // Footer sections
            const footerY = summaryY + 60;

            // Credits section
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.text("CREDITS:", 40, footerY);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.text("If you are due a credit or refund, the Department will calculate that amount for you and will send you a notice of refund due.", 90, footerY);

            // Penalties section - FIXED
            doc.setFont("helvetica", "bold");
            doc.text("PENALTIES AND INTEREST:", 40, footerY + 15);
            doc.setFont("helvetica", "normal");
            const penaltiesText = "If your tax return and payment are late or you do not pay the amount of tax due, the Department will calculate penalty and interest for you and will send you a notice of any deficiencies after this return is filed.";
            const penaltiesLines = doc.splitTextToSize(penaltiesText, pageWidth - 100);
            doc.text(penaltiesLines, 75, footerY + 25); // Added proper spacing

            // Due date section - FIXED
            doc.setFont("helvetica", "bold");
            doc.text("TAX RETURN DUE DATE AND FILING:", 40, footerY + 45); // Added proper spacing
            doc.setFont("helvetica", "normal");
            const dueDateText = "This tax return MUST BE SIGNED and FILED along with Schedule A (if applicable) at the address above along with payment for taxes due by the due date to be considered timely filed, otherwise, penalties and interest will be applied. If mailed, the return must be postmarked by the due date for the return. If the last day falls on a Saturday, Sunday or legal holiday, the return and payment of tax shall be due at the close of the next business day.";
            const dueDateLines = doc.splitTextToSize(dueDateText, pageWidth - 100);
            doc.text(dueDateLines, 75, footerY + 55); // Added proper spacing

            // Signature section - ADJUSTED for spacing
            const signatureY = footerY + 105; // Increased to account for additional text lines
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            const declarationText = "I DECLARE, UNDER PENALTY OF PERJURY, THAT I HAVE EXAMINED THIS RETURN AND THE INFORMATION CONTAINED HEREIN, INCLUDING ACCOMPANYING SCHEDULES AND STATEMENTS, AND TO THE BEST OF MY KNOWLEDGE AND BELIEF THEY ARE TRUE, CORRECT AND COMPLETE.";
            const declarationLines = doc.splitTextToSize(declarationText, pageWidth - 80);
            doc.text(declarationLines, 40, signatureY);

            doc.text("DECLARATION OF PREPARER (OTHER THAN TAXPAYER) IS BASED ON ALL INFORMATION OF WHICH PREPARER HAS ANY KNOWLEDGE.", 40, signatureY + 30);

            // Signature lines
            doc.setLineWidth(0.5);
            
            // Add signature image if available (place it above the signature line)
            if (signatureDataURL) {
                try {
                    // Helper function to process and add signature image
                    const processSignatureImage = (img) => {
                        // Available space: width 160pt (200-40), reduced height to avoid overlapping text above
                        // Text ends around signatureY + 30, so signature should start after that
                        const maxWidth = 160;
                        const maxHeight = 20; // Reduced height to prevent overlap with text above
                        
                        // Calculate aspect ratio
                        const aspectRatio = img.width / img.height;
                        
                        // Calculate dimensions to fit within maxWidth and maxHeight while maintaining aspect ratio
                        let signatureWidth = maxWidth;
                        let signatureHeight = maxWidth / aspectRatio;
                        
                        // If height exceeds maxHeight, scale down based on height
                        if (signatureHeight > maxHeight) {
                            signatureHeight = maxHeight;
                            signatureWidth = maxHeight * aspectRatio;
                        }
                        
                        // Position signature above the line, starting after the text (signatureY + 30)
                        // Leave some space after text, position closer to the line at signatureY + 50
                        const signatureX = 40;
                        const signatureYPos = signatureY + 38; // Moved lower, closer to the line
                        
                        // Add signature image with calculated dimensions
                        doc.addImage(signatureDataURL, 'PNG', signatureX, signatureYPos, signatureWidth, signatureHeight);
                    };
                    
                    // Load image to get dimensions and scale proportionally
                    const img = new Image();
                    
                    await new Promise((resolve, reject) => {
                        img.onload = () => {
                            try {
                                processSignatureImage(img);
                                resolve();
                            } catch (error) {
                                console.error('Error processing signature image:', error);
                                reject(error);
                            }
                        };
                        img.onerror = (error) => {
                            console.error('Error loading signature image:', error);
                            reject(error);
                        };
                        
                        // Set src after setting up handlers
                        img.src = signatureDataURL;
                        
                        // If image is already loaded (complete), process immediately
                        if (img.complete && img.naturalWidth > 0) {
                            try {
                                processSignatureImage(img);
                                resolve();
                            } catch (error) {
                                console.error('Error processing signature image:', error);
                                reject(error);
                            }
                        }
                    });
                } catch (error) {
                    console.error('Error adding signature image to PDF:', error);
                }
            }
            
            doc.line(40, signatureY + 50, 200, signatureY + 50);
            doc.text("Taxpayer's Signature (Required)", 40, signatureY + 65);

            doc.line(220, signatureY + 50, 350, signatureY + 50);
            doc.text("Date", 220, signatureY + 65);

            // Add current date (MM/dd/YYYY format)
            const currentDate = new Date();
            const month = String(currentDate.getMonth() + 1).padStart(2, '0');
            const day = String(currentDate.getDate()).padStart(2, '0');
            const formattedDate = `${month}/${day}/${currentDate.getFullYear()}`;
            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            doc.text(formattedDate, 220, signatureY + 45);

            doc.line(370, signatureY + 50, 500, signatureY + 50);
            doc.text("Paid Preparer's Signature", 370, signatureY + 65);

            // Payment instructions
            doc.setFont("helvetica", "bold");
            doc.setFontSize(9);
            doc.text('Please make check payable to "CRIT Department of Revenue". NO CASH ACCEPTED.', 40, signatureY + 90);
            doc.setFont("helvetica", "normal");
            doc.text("Please mail or return your completed return and tax payment to:", 40, signatureY + 105);
            doc.text("CRIT DEPARTMENT OF REVENUE, 26600 Mohave Road, Parker, AZ 85344 Phone: (928) 575-1532 Email: taxinfo@crit-nsn.gov", 40, signatureY + 120);

            // Footer
            doc.setFontSize(8);
            // doc.text("FORM TR-1 and Schedule A (Revised 03/20)", 40, pageHeight - 20);
            // doc.text("(Continued on other side)", pageWidth - 100, pageHeight - 20);
            // doc.text("Page 1 of 2", pageWidth - 50, pageHeight - 20);
        };

        // PAGE 2 - SCHEDULE A with centered header text
        const drawPage2 = () => {
            doc.addPage();

            // Header
            doc.setFont("helvetica", "bold");
            doc.setFontSize(14);
            doc.text("SCHEDULE A to FORM TR-1", 40, 40);

            // Taxpayer and Reporting Period
            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            doc.text("Taxpayer Name or Lic. No.:", 40, 65);
            doc.line(160, 70, 300, 70);
            doc.text(pdfTaxPayerName || "", 165, 65);

            doc.text("Reporting Period:", 320, 65);
            doc.line(390, 70, 500, 70);
            if (reportingPeriod) {
                doc.text(reportingPeriod, 400, 65);
            }

            // Instructions
            doc.setFontSize(9);
            const instructionText = "Use Schedule A to complete Form TR-1 Section II, Column 2. The total of exemptions/exclusions on Schedule A must match the amount in Form TR-1 Section II, Column 2.";
            const instructionLines = doc.splitTextToSize(instructionText, pageWidth - 80);
            doc.text(instructionLines, 40, 85);

            // Schedule A Tables - Left side (Compact layout)
            const leftStartY = 110;
            const tableWidth = 240;
            const codeColWidth = 50;
            const amountColWidth = 50;
            const rowHeight = 12;

            const scheduleSections = [
                {
                    title: "Sales Tax (S or Ex Codes Only)",
                    line: 1,
                    key: "Sales Tax"
                },
                {
                    title: "Sales Tax Exceeding $2500 Single Item (S or Ex Codes Only)",
                    line: 2,
                    key: "Sales Tax (Excess of $2500 on Single Item Sale)"
                },
                {
                    title: "Restaurant Privilege Tax (R or Ex Codes Only)",
                    line: 3,
                    key: "Restaurant Privilege"
                },
                {
                    title: "Transient Lodging Tax (TL or Ex Codes Only)",
                    line: 4,
                    key: "Transient Lodging Tax"
                },
                {
                    title: "Utility Business Activity Tax (U or Ex Codes Only)",
                    line: 5,
                    key: "Utility Business Activity Tax"
                },
                {
                    title: "Liquor Privilege Tax (L or Ex Codes Only)",
                    line: 6,
                    key: "Liquor Privilege Tax"
                },
                {
                    title: "Tobacco Products Tax (Ex Codes Only)",
                    line: 7,
                    key: "Tobacco Products Tax"
                }
            ];

            let currentTableY = leftStartY;
            let currentPage = 2;
            let hasMoreData = false;

            // Function to draw a single section with pagination
            const drawSectionWithPagination = (section, startY) => {
                let currentSectionY = startY;

                // Section title (split if too long)
                doc.setFont("helvetica", "bold");
                doc.setFontSize(8);
                const titleLines = doc.splitTextToSize(section.title, tableWidth - 10);
                doc.text(titleLines, 40, currentSectionY);

                const titleHeight = titleLines.length * 10;

                // Table header with CENTERED text
                doc.setFillColor(240, 240, 240);
                doc.rect(40, currentSectionY + titleHeight, tableWidth, rowHeight, 'F');
                doc.rect(40, currentSectionY + titleHeight, tableWidth, rowHeight);

                // Code header - CENTERED
                const codeCenterX = 40 + (codeColWidth / 2);
                doc.text("Code", codeCenterX, currentSectionY + titleHeight + 8, { align: 'center' });

                // Amount header - CENTERED
                const amountCenterX = 40 + codeColWidth + (amountColWidth / 2);
                doc.text("Amount", amountCenterX, currentSectionY + titleHeight + 8, { align: 'center' });

                doc.line(40 + codeColWidth, currentSectionY + titleHeight, 40 + codeColWidth, currentSectionY + titleHeight + rowHeight);

                // Get exemption data for this section
                const sectionExemptions = exemptionData[section.key] || [];
                let totalSectionAmount = 0;

                // Calculate how many rows we need
                const totalRowsNeeded = Math.max(4, sectionExemptions.length); // Minimum 4 rows, more if needed

                // Draw rows dynamically
                for (let i = 0; i < totalRowsNeeded; i++) {
                    const rowY = currentSectionY + titleHeight + rowHeight + (i * rowHeight);

                    // Check if we need a new page
                    if (rowY + rowHeight > pageHeight - 50) {
                        // Add new page
                        doc.addPage();
                        currentPage++;

                        // Reset Y position for new page
                        currentSectionY = 40;

                        // Redraw section header on new page
                        doc.setFont("helvetica", "bold");
                        doc.setFontSize(8);
                        const newTitleLines = doc.splitTextToSize(section.title + " (continued)", tableWidth - 10);
                        doc.text(newTitleLines, 40, currentSectionY);

                        // Redraw table header
                        doc.setFillColor(240, 240, 240);
                        doc.rect(40, currentSectionY + titleHeight, tableWidth, rowHeight, 'F');
                        doc.rect(40, currentSectionY + titleHeight, tableWidth, rowHeight);
                        doc.text("Code", codeCenterX, currentSectionY + titleHeight + 8, { align: 'center' });
                        doc.text("Amount", amountCenterX, currentSectionY + titleHeight + 8, { align: 'center' });
                        doc.line(40 + codeColWidth, currentSectionY + titleHeight, 40 + codeColWidth, currentSectionY + titleHeight + rowHeight);
                    }

                    // Draw row border
                    doc.rect(40, rowY, tableWidth, rowHeight);
                    doc.line(40 + codeColWidth, rowY, 40 + codeColWidth, rowY + rowHeight);

                    // Fill with exemption data if available
                    if (i < sectionExemptions.length) {
                        const exemption = sectionExemptions[i];
                        doc.setFont("helvetica", "normal");
                        doc.setFontSize(7);

                        // Code (centered)
                        const codeX = 40 + (codeColWidth / 2);
                        doc.text(exemption.code, codeX, rowY + 8, { align: 'center' });

                        // Amount (centered)
                        const amountX = 40 + codeColWidth + (amountColWidth / 2);
                        doc.text(exemption.amount.toFixed(2), amountX, rowY + 8, { align: 'center' });

                        totalSectionAmount += exemption.amount;
                    }
                }

                // Total line - check if we need new page for total
                let totalY = currentSectionY + titleHeight + rowHeight + (totalRowsNeeded * rowHeight);

                if (totalY + 35 > pageHeight - 50) {
                    doc.addPage();
                    currentPage++;
                    totalY = 40;
                }

                doc.setFont("helvetica", "bold");
                doc.setFontSize(8);
                doc.text("Total Exemptions/Exclusions", 45, totalY + 8);
                doc.setFont("helvetica", "normal");
                doc.text(`(Enter on Section II, Line ${section.line}, Column 2)`, 45, totalY + 16);

                // Display total amount for this section
                if (totalSectionAmount > 0) {
                    doc.setFont("helvetica", "bold");
                    doc.setFontSize(8);
                    const totalAmountX = 100 + codeColWidth + (amountColWidth / 2);
                    doc.text(totalSectionAmount.toFixed(2), totalAmountX, totalY + 8, { align: 'center' });
                }

                return totalY + 35; // Return next Y position
            };

            // Draw all sections with pagination
            scheduleSections.forEach((section, index) => {
                // Check if we need a new page before starting this section
                if (currentTableY > pageHeight - 150 && index > 0) {
                    doc.addPage();
                    currentPage++;
                    currentTableY = 40;
                }

                currentTableY = drawSectionWithPagination(section, currentTableY);
            });

            // Draw exemption codes on the right side (only if there's space on current page)
            const drawExemptionCodes = (startY) => {
                const rightStartX = 300;
                const rightWidth = pageWidth - rightStartX - 40;

                // Exemption Codes - CENTERED header
                doc.setFont("helvetica", "bold");
                doc.setFontSize(10);
                const exemptionCodesCenterX = rightStartX + (rightWidth / 2);
                doc.text("Exemption Codes", exemptionCodesCenterX, startY, { align: 'center' });

                const exemptionCodes = [
                    ["Tangible property sold at wholesale to a retailer", "S01"],
                    ["Prepared food items for immediate consumption", "S02"],
                    ["Legend drugs on prescription", "S03"],
                    ["Food items sold by an eligible grocery business", "S04"],
                    ["Tangible personal property sold by 501(c)(3)", "S05"],
                    ["Prescription eyeglasses, contacts, frames and eye exams", "S06"],
                    ["Prosthetic appliances or devices", "S07"],
                    ["Insulin, insulin syringes and glucose test strips", "S08"],
                    ["Hearing aids and any parts, except batteries and cords", "S09"],
                    ["Food stamp purchases", "S10"],
                    ["Food, drink or accessory property sold to a school district", "S11"],
                    ["Seeds, roots and bulbs used in commercial production", "S12"],
                    ["Machinery and equipment for physically disabled persons", "S13"],
                    ["Livestock and poultry feed used in commercial production", "S14"],
                    ["Livestock and poultry growth promotants", "S15"],
                    ["Agricultural crops or produce sold by grower to broker/retailer", "S16"],
                    ["Durable medical equipment", "S17"],
                    ["New Farm machinery and equipment", "S18"],
                    ["Prepared food items for immediate consumption sold by non-restaurant retailers", "R01"],
                    ["Transient lodging units occupied for more than 30 days", "TL01"],
                    ["Establishments with fewer than 4 transient lodging units", "TL02"],
                    ["Utility sales at wholesale", "U01"],
                    ["Liquor sales not occuring at liquor retailer business", "L01"]
                ];

                let currentCodeY = startY + 12;
                doc.setFont("helvetica", "normal");
                doc.setFontSize(7);

                exemptionCodes.forEach((code, index) => {
                    if (currentCodeY > pageHeight - 50) {
                        // If we run out of space, mark that we have more data and stop
                        hasMoreData = true;
                        return;
                    }

                    const description = code[0];
                    const codeValue = code[1];

                    // Split long descriptions
                    const lines = doc.splitTextToSize(description, rightWidth - 50);
                    doc.text(lines, rightStartX, currentCodeY);
                    doc.text(codeValue, rightStartX + rightWidth - 30, currentCodeY, { align: 'right' });

                    currentCodeY += (lines.length * 8) + 3;
                });

                // Exclusion Codes - only if there's space
                const exclusionStartY = currentCodeY + 10;

                if (exclusionStartY < pageHeight - 80) {
                    doc.setFont("helvetica", "bold");
                    doc.setFontSize(10);
                    const exclusionCodesCenterX = rightStartX + (rightWidth / 2);
                    doc.text("Exclusion Codes", exclusionCodesCenterX, exclusionStartY, { align: 'center' });

                    const exclusionCodes = [
                        ["Refunds to Consumer", "Ex01"],
                        ["Other Tax Collected", "Ex02"],
                        ["Repair and Installation Labor", "Ex03"],
                        ["Sales not subject to CRIT Tax", "Ex04"],
                        ["Shipping Costs", "Ex05"]
                    ];

                    let currentExclusionY = exclusionStartY + 12;
                    doc.setFont("helvetica", "normal");
                    doc.setFontSize(7);

                    exclusionCodes.forEach((code) => {
                        if (currentExclusionY > pageHeight - 50) return;

                        const description = code[0];
                        const codeValue = code[1];

                        doc.text(description, rightStartX, currentExclusionY);
                        doc.text(codeValue, rightStartX + rightWidth - 30, currentExclusionY, { align: 'right' });

                        currentExclusionY += 10;
                    });
                }

                return currentCodeY;
            };

            // Draw exemption codes on the right side if there's space
            if (currentTableY < pageHeight - 200) {
                drawExemptionCodes(currentTableY);
            } else {
                // If no space, add a new page for exemption codes
                doc.addPage();
                currentPage++;
                drawExemptionCodes(40);
            }

            // If we have more exemption codes that didn't fit, add continuation pages
            if (hasMoreData) {
                doc.addPage();
                currentPage++;

                // Header for continuation page
                doc.setFont("helvetica", "bold");
                doc.setFontSize(14);
                doc.text("SCHEDULE A to FORM TR-1 (Continued)", 40, 40);

                // Continue drawing remaining exemption codes
                const rightStartX = 40;
                const rightWidth = pageWidth - 80;
                let currentCodeY = 60;

                doc.setFont("helvetica", "bold");
                doc.setFontSize(12);
                doc.text("Exemption Codes (Continued)", rightStartX, currentCodeY);
                currentCodeY += 20;

                const remainingExemptionCodes = [
                    // Add any remaining codes that didn't fit on previous pages
                    ["Prepared food items for immediate consumption sold by non-restaurant retailers", "R01"],
                    ["Transient lodging units occupied for more than 30 days", "TL01"],
                    ["Establishments with fewer than 4 transient lodging units", "TL02"],
                    ["Utility sales at wholesale", "U01"],
                    ["Liquor sales not occuring at liquor retailer business", "L01"]
                ];

                doc.setFont("helvetica", "normal");
                doc.setFontSize(9);

                remainingExemptionCodes.forEach((code) => {
                    if (currentCodeY > pageHeight - 50) {
                        doc.addPage();
                        currentPage++;
                        currentCodeY = 40;
                    }

                    const description = code[0];
                    const codeValue = code[1];

                    const lines = doc.splitTextToSize(description, rightWidth - 50);
                    doc.text(lines, rightStartX, currentCodeY);
                    doc.text(codeValue, rightStartX + rightWidth - 30, currentCodeY, { align: 'right' });

                    currentCodeY += (lines.length * 12) + 5;
                });

                // Add exclusion codes if they didn't fit
                if (currentCodeY < pageHeight - 100) {
                    currentCodeY += 15;
                    doc.setFont("helvetica", "bold");
                    doc.setFontSize(12);
                    doc.text("Exclusion Codes", rightStartX, currentCodeY);
                    currentCodeY += 15;

                    const exclusionCodes = [
                        ["Refunds to Consumer", "Ex01"],
                        ["Other Tax Collected", "Ex02"],
                        ["Repair and Installation Labor", "Ex03"],
                        ["Sales not subject to CRIT Tax", "Ex04"],
                        ["Shipping Costs", "Ex05"]
                    ];

                    doc.setFont("helvetica", "normal");
                    doc.setFontSize(9);

                    exclusionCodes.forEach((code) => {
                        if (currentCodeY > pageHeight - 50) {
                            doc.addPage();
                            currentPage++;
                            currentCodeY = 40;
                        }

                        const description = code[0];
                        const codeValue = code[1];

                        doc.text(description, rightStartX, currentCodeY);
                        doc.text(codeValue, rightStartX + rightWidth - 30, currentCodeY, { align: 'right' });

                        currentCodeY += 12;
                    });
                }
            }

            // Update footer with current page number
            doc.setFontSize(8);
            // doc.text(`Page ${currentPage} of ${currentPage}`, pageWidth - 50, pageHeight - 20);
        };

        // Generate both pages
        await drawPage1();
        drawPage2();

        // Return the PDF document instead of saving it
        return doc;
    };

    // Helper function to convert PDF to Blob
    const pdfToBlob = (pdf) => {
        const pdfOutput = pdf.output('blob');
        return pdfOutput;
    };

    // Helper function to convert base64 data URL to File object
    const dataURLtoFile = (dataurl, filename) => {
        const arr = dataurl.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);

        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }

        return new File([u8arr], filename, { type: mime });
    };

    // Updated confirmSubmit (capture data before reset)
    const confirmSubmit = async () => {
        // Ensure PDF has data even if we short-circuit submission
        // Make sure reportingPeriodMonth and reportingPeriodYear are included with defaults if missing
        // Get signature from canvas for submittedData
        const signatureDataURLForStorage = sigPad.current?.getTrimmedCanvas().toDataURL('image/png') || null;
        setSubmittedData({
            formData: {
                ...formData,
                tribalLicenseNo: formData.tribalLicenseNo || '',
                reportingPeriodMonth: formData.reportingPeriodMonth || defaultDate.month,
                reportingPeriodYear: formData.reportingPeriodYear || defaultDate.year
            },
            sectionGroups: [...sectionGroups],
            taxPayerName,
            signatureDataURL: signatureDataURLForStorage
        });
        ////////
        // setShowDownloadPopup(true);
        // setShowConfirmation(false);

        // return;
        //////////
        setShowConfirmation(false);
        setIsAPILoading(true);
        setError(null);

        const requestData = prepareRequestData();

        // Debug: Log the prepared data
        console.log("Prepared request data:", JSON.stringify(requestData, null, 2));
      

        const taxpayerId = localStorage.getItem("selectedTaxPayer") || '';
        const fetchUrl = `${UTILS}?action=taxreturn&taxpayer=${taxpayerId}`;

        try {
            // Use signature already captured at the start of the function
            // Verify signature exists and is not empty
            let signatureFile = null;
            if (signatureDataURLForStorage) {
                // Check if signature canvas is not empty (has actual content)
                const isSignatureEmpty = sigPad.current?.isEmpty();
                if (!isSignatureEmpty) {
                    signatureFile = dataURLtoFile(signatureDataURLForStorage, 'signature.png');
                    console.log('Signature file created:', signatureFile ? `Size: ${signatureFile.size} bytes` : 'Failed to create');
                } else {
                    console.warn('Signature canvas is empty');
                }
            } else {
                console.warn('No signature data URL available');
            }

            // Find the Month field's fieldname to use as fallback
            const monthField = formSections?.taxrevenue?.fields?.find(
                field => field.displayname === "Month"
            );
            const monthFieldValue = monthField ? formData[monthField.fieldname] : null;
            
            // Find the Year field's fieldname to use as fallback
            const yearField = formSections?.taxrevenue?.fields?.find(
                field => field.displayname === "Year"
            );
            const yearFieldValue = yearField ? formData[yearField.fieldname] : null;

            // Generate PDF
            const pdf = await generatePDF(
                {
                    ...formData,
                    tribalLicenseNo: formData.tribalLicenseNo || '',
                    reportingPeriodMonth: formData.reportingPeriodMonth || monthFieldValue || defaultDate.month,
                    reportingPeriodYear: formData.reportingPeriodYear || yearFieldValue || defaultDate.year
                },
                sectionGroups,
                taxPayerName,
                signatureDataURLForStorage
            );

            // Convert PDF to Blob and then to File
            const pdfBlob = pdfToBlob(pdf);
            const pdfFile = new File([pdfBlob], `Tax_Return_${formData.reportingPeriodMonth || defaultDate.month}_${formData.reportingPeriodYear || defaultDate.year}_${taxpayerId || 'unknown'}.pdf`, {
                type: 'application/pdf'
            });

            // Create FormData - send requestData as JSON string in 'Body' field, and files separately
            const formDataToSend = new FormData();
            formDataToSend.append('Body', JSON.stringify(requestData));
            if (signatureFile) {
                console.log('Appending signature to FormData:', {
                    name: signatureFile.name,
                    size: signatureFile.size,
                    type: signatureFile.type
                });
                formDataToSend.append('Signature', signatureFile, 'signature.png');
            } else {
                console.warn('WARNING: No signature file to append to FormData');
            }
            formDataToSend.append('PDF', pdfFile, `Tax_Return_${formData.reportingPeriodMonth || defaultDate.month}_${formData.reportingPeriodYear || defaultDate.year}_${taxpayerId || 'unknown'}.pdf`);

            // Print FormData contents to console
            console.log("FormData contents:");
            for (let pair of formDataToSend.entries()) {
                if (pair[0] === 'PDF' || pair[0] === 'Signature') {
                    console.log(`${pair[0]}:`, pair[1], `(File: ${pair[1].name}, Size: ${pair[1].size} bytes, Type: ${pair[1].type})`);
                } else {
                    console.log(`${pair[0]}:`, pair[1]);
                }
            }


            // //testing
            // setShowDownloadPopup(true);
            // return;

            // //

            const response = await fetch(fetchUrl, {
                method: "POST",
                body: formDataToSend
            });



            // Handle potential BOM and control characters in response
            const text = await response.text();
            console.log("API Response - Raw text:", text);
            const cleanedText = text.replace(/^[\uFEFF\x00-\x1F\x7F-\x9F]+|[\uFEFF\x00-\x1F\x7F-\x9F]+$/g, "");
            console.log("API Response - Cleaned text:", cleanedText);
            let data;
            try {
                data = JSON.parse(cleanedText);
                console.log("API Response - Parsed data:", data);
            } catch (parseError) {
                console.error("Failed to parse response:", {
                    error: parseError,
                    responseText: text,
                    cleanedText: cleanedText
                });
                throw new Error("Invalid server response format.");
            }

            if (data.status === "success") {
                // Capture data BEFORE resetting
                // Make sure reportingPeriodMonth and reportingPeriodYear are included with defaults if missing
                // Reuse signature already captured at the start of the function
                setSubmittedData({
                    formData: {
                        ...formData,
                        tribalLicenseNo: formData.tribalLicenseNo || '',
                        reportingPeriodMonth: formData.reportingPeriodMonth || defaultDate.month,
                        reportingPeriodYear: formData.reportingPeriodYear || defaultDate.year
                    },
                    sectionGroups: [...sectionGroups],
                    taxPayerName,
                    signatureDataURL: signatureDataURLForStorage
                });

                setShowDownloadPopup(true);

                // Now reset form data
                const resetFormData = {};
                formSections.taxrevenue.fields.forEach(field => {
                    if (field.defaultvalue) {
                        resetFormData[field.fieldname] = field.defaultvalue;
                    } else if (field.displayname === "Month") {
                        resetFormData[field.fieldname] = defaultDate.month;
                    } else if (field.displayname === "Year") {
                        resetFormData[field.fieldname] = defaultDate.year;
                    } else {
                        resetFormData[field.fieldname] = "";
                    }
                });

                // Preserve default values for tribalLicenseNo, reportingPeriodMonth, and reportingPeriodYear
                // Load Tribal License Number from localStorage if available, otherwise use empty string
                const tribalLicenseNumber = localStorage.getItem("tribalLicenseNumber");
                resetFormData.tribalLicenseNo = tribalLicenseNumber || '';
                resetFormData.reportingPeriodMonth = defaultDate.month;
                resetFormData.reportingPeriodYear = defaultDate.year;

                // Reset section groups
                setSectionGroups([{
                    id: 1,
                    exemptionGroups: [1],
                    splitpayment: sectionGroups[0].splitpayment,
                    exemption: sectionGroups[0].exemption
                }]);

                if (exemptionCodeFieldName) {
                    resetFormData[`${exemptionCodeFieldName}_1_1`] = "";
                }
                if (exemptionAmountFieldName) {
                    resetFormData[`${exemptionAmountFieldName}_1_1`] = "";
                }
                if (exemptionTaxTypeFieldName) {
                    resetFormData[`${exemptionTaxTypeFieldName}_1_1`] = "";
                }

                setFormData(resetFormData);
                if (sigPad.current) sigPad.current.clear();
                setFiles([{ id: 1, file: null, type: "" }]);
                setDate("");
                setIsSigned(false);
            } else {
                throw new Error(data.message || "Submission failed.");
            }
        } catch (error) {
            console.error("Submission error:", {
                error: error,
                message: error.message,
                stack: error.stack
            });
            setError(error.message || "An unknown error occurred.");
        } finally {
            setIsAPILoading(false);
        }
    };

    // Signature handling functions
    const clearSignature = () => {
        if (sigPad.current) {
            sigPad.current.clear();
            setIsSigned(false);
        }
    };

    const handleSignatureBegin = () => {
        setIsSigned(true);
    };

    const handleSignatureEnd = () => {
        if (sigPad.current && !sigPad.current.isEmpty()) {
            setIsSigned(true);
        }
    };

    // Initialize canvas size after component mounts
    useEffect(() => {
        const updateCanvasSize = () => {
            const container = document.getElementById('signature-container-taxreturn');
            if (container) {
                const width = container.clientWidth - 32;
                const height = 200;
                setCanvasSize({ width, height });

                if (sigPad.current) {
                    const isEmpty = sigPad.current.isEmpty();
                    if (!isEmpty) {
                        const data = sigPad.current.toData();
                        sigPad.current.clear();
                        setTimeout(() => {
                            if (sigPad.current) {
                                sigPad.current.fromData(data);
                            }
                        }, 0);
                    }
                }
            }
        };

        // Call immediately
        updateCanvasSize();

        // Also call after a short delay to ensure DOM is ready
        const timer = setTimeout(() => {
            updateCanvasSize();
        }, 100);

        window.addEventListener('resize', updateCanvasSize);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', updateCanvasSize);
        };
    }, []);

    // Additional useEffect to update canvas size when form sections change (to catch late rendering)
    useEffect(() => {
        if (!isLoading) {
            const timer = setTimeout(() => {
                const container = document.getElementById('signature-container-taxreturn');
                if (container) {
                    const width = container.clientWidth - 32;
                    const height = 200;
                    setCanvasSize({ width, height });
                }
            }, 200);
            return () => clearTimeout(timer);
        }
    }, [isLoading]);

    const handleSubmit = (e) => {
        e.preventDefault();

        // Check if signature is provided (mandatory)
        if (!isSigned || (sigPad.current && sigPad.current.isEmpty())) {
            alert('Please provide a signature before submitting the form.');
            return;
        }

        setShowConfirmation(true);
    };

    const clearExemptionCodesForGroup = (groupId) => {
        setFormData(prev => {
            const newData = { ...prev };

            // Find all exemption code fields for this group and clear them
            Object.keys(prev).forEach(key => {
                if (key.startsWith(`${exemptionCodeFieldName}_${groupId}_`)) {
                    newData[key] = "";
                }

                // Optionally clear exemption amounts too
                if (key.startsWith(`${exemptionAmountFieldName}_${groupId}_`)) {
                    newData[key] = "";
                }
            });

            return newData;
        });

        // Also clear the exemption code options cache for this group
        setExemptionCodeOptions(prev => {
            const newOptions = { ...prev };
            Object.keys(newOptions).forEach(key => {
                if (key.startsWith(`${groupId}_`)) {
                    delete newOptions[key];
                }
            });
            return newOptions;
        });
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name.includes(splitPaymentFieldNames['Tax Type'])) {
            // Extract group ID from the field name
            const groupId = name.split('_').pop();

            // Clear all exemption codes for this group
            clearExemptionCodesForGroup(groupId);
        }

        if (name === 'frc') {
            setInputValue(value);

            if (value.length >= 3) {
                fetchZipData(value);
                setShowZipDropdown(true);
            } else {
                setShowZipDropdown(false);
                setFormData((prevFormData) => ({ ...prevFormData, frc: "" }));
            }
        } else if (type === 'checkbox') {
            setFormData((prevFormData) => {
                const currentValues = prevFormData[name] || [];
                let updatedValues;

                if (checked) {
                    updatedValues = [...currentValues, value];
                } else {
                    updatedValues = currentValues.filter((item) => item !== value);
                }

                return {
                    ...prevFormData,
                    [name]: updatedValues,
                };
            });
        } else {
            // Check if this is the Month field and sync to reportingPeriodMonth
            const isMonthField = formSections?.taxrevenue?.fields?.some(
                field => field.fieldname === name && field.displayname === "Month"
            );
            
            // Check if this is the Year field and sync to reportingPeriodYear
            const isYearField = formSections?.taxrevenue?.fields?.some(
                field => field.fieldname === name && field.displayname === "Year"
            );

            setFormData((prevFormData) => {
                const updatedData = {
                    ...prevFormData,
                    [name]: value,
                };
                
                // Sync Month field to reportingPeriodMonth
                if (isMonthField) {
                    updatedData.reportingPeriodMonth = value;
                }
                
                // Sync Year field to reportingPeriodYear
                if (isYearField) {
                    updatedData.reportingPeriodYear = value;
                }
                
                return updatedData;
            });
        }
    };

    const fetchZipData = async (zip) => {
        try {
            const response = await fetch(UTILS, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    "Accept": "application/json"
                },
                body: new URLSearchParams({ action: "zip", s: zip }),
            });
            const text = await response.text();
            const cleanedText = text.replace(/[\x00-\x1F\x7F-\x9F]/g, "");

            let data;
            try {
                data = JSON.parse(cleanedText);
            } catch (parseError) {
                console.error("Failed to parse ZIP data:", parseError);
                throw new Error("Invalid ZIP data format.");
            }

            if (response.ok) {
                setZipCodes(data);
            } else {
                throw new Error(data.message || "Failed to fetch ZIP codes.");
            }
        } catch (error) {
            console.error("ZIP lookup failed:", error);
            setZipCodes([]);
        }
    };

    const handleZipSelection = (zip) => {
        setInputValue(zip.zipcode);
        setFormData((prevFormData) => ({ ...prevFormData, frc: zip.itemid }));
        setShowZipDropdown(false);
    };

    const formatPhoneOnChange = (e) => {
        const input = e.target;
        let phoneNumber = input.value.replace(/\D/g, '');
        if (phoneNumber.length > 10) {
            phoneNumber = phoneNumber.slice(0, 10);
        }

        let formattedPhone = phoneNumber;
        if (phoneNumber.length > 6) {
            formattedPhone = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
        } else if (phoneNumber.length > 3) {
            formattedPhone = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
        } else if (phoneNumber.length > 0) {
            formattedPhone = `(${phoneNumber}`;
        }

        input.value = formattedPhone;
        setFormData((prevFormData) => ({ ...prevFormData, [input.name]: formattedPhone }));
    };

    const formatSSNOnChange = (e) => {
        const input = e.target.value.replace(/\D/g, "");
        let formattedValue = "";

        if (input.length > 0) {
            formattedValue += input.substring(0, 3);
        }
        if (input.length > 3) {
            formattedValue += "-" + input.substring(3, 5);
        }
        if (input.length > 5) {
            formattedValue += "-" + input.substring(5, 9);
        }

        setFormData({ ...formData, [e.target.name]: formattedValue });
    };

    const handleCheckboxGroupChange = (e, fieldName) => {
        const { value, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [fieldName]: checked
                ? [...(prev[fieldName] || []), value]
                : (prev[fieldName] || []).filter(item => item !== value)
        }));
    };

    const handleClear = () => {
        if (sigPad.current) {
            sigPad.current.clear();
            setIsSigned(false);
        }
    };

    const handleEnd = () => {
        if (sigPad.current) {
            setIsSigned(!sigPad.current.isEmpty());
        }
    };

    const handleFileTypeChange = (index, value) => {
        const updatedFiles = [...files];
        updatedFiles[index].type = value;
        setFiles(updatedFiles);
    };

    const handleAddMore = () => {
        setFiles([...files, { id: files.length + 1, file: null, type: "" }]);
    };

    const handleFileChange = (index, event) => {
        const updatedFiles = [...files];
        updatedFiles[index].file = event.target.files[0];
        setFiles(updatedFiles);
    };

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center h-64 space-y-4">
                <div className="relative">
                    <div className="w-16 h-16 rounded-full absolute border-4 border-gray-200"></div>
                    <div className="w-16 h-16 rounded-full animate-spin border-4 border-primary border-t-transparent border-r-transparent"></div>

                    <div className="absolute inset-0 flex items-center justify-center">
                        <svg
                            className="w-6 h-6 text-primary"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 14l-7 7m0 0l-7-7m7 7V3"
                            ></path>
                        </svg>
                    </div>
                </div>

                <div className="flex flex-col items-center space-y-2">
                    <p className="text-gray-600 font-medium text-lg animate-pulse">Preparing Your Form...</p>
                    <div className="flex space-x-2">
                        {[...Array(3)].map((_, i) => (
                            <div
                                key={i}
                                className="w-2 h-2 bg-primary rounded-full animate-bounce"
                                style={{ animationDelay: `${i * 0.1}s` }}
                            ></div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
                <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading Form</h2>
                <p className="text-gray-700 mb-4">{error}</p>
                <button
                    onClick={() => window.location.reload()}
                    className="bg-[#711515] text-white px-6 py-2 rounded-lg hover:bg-[#5a1010] transition"
                >
                    Try Again
                </button>
            </div>
        );
    }

    return (
        <div className="w-[95%] md:w-[80%] p-4 md:p-8 mx-auto bg-white mt-5 rounded-lg shadow-lg">
            {error && (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
                    <p>{error}</p>
                </div>
            )}

            {/* Form Header */}
            <div className="mb-6 text-center">
                <h1 className="text-3xl font-bold text-gray-800 mb-4">FORM TR-1 TAX RETURN</h1>
                <div className="text-base text-gray-800 space-y-1">
                    <p>Colorado River Indian Tribes</p>
                    <p>26600 Mohave Road</p>
                    <p>Parker, Arizona 85344</p>
                    <p>Ph.: (928) 575-1532</p>
                    <p>Email: taxinfo@crit-nsn.gov</p>
                </div>
            </div>

            {/* Tribal License No. Section */}
            <div className="mb-8 bg-gray-50 p-6 rounded-lg border border-gray-300">
                <div>
                    {/* Tribal License No. */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Tribal License No.
                        </label>
                        <input
                            type="text"
                            value={formData.tribalLicenseNo || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, tribalLicenseNo: e.target.value }))}
                            className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#711515] focus:border-[#711515] bg-white text-gray-900"
                            placeholder="Enter Tribal License No."
                        />
                    </div>
                </div>
            </div>

            {/* Tax Revenue Information (always shown once) */}
            <div key="taxrevenue" className="flex flex-col gap-6 mb-8">


                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {formSections.taxrevenue.fields
                        .filter(
                            (field) =>
                                ![
                                    "itemid",
                                    "autonumber",
                                    "readonlydate",
                                    "readonlypicklist",
                                    "readonly",
                                    "hidden",
                                    "readonlylookup",
                                    "linkedfield",
                                    "inlinequery",
                                    "person",
                                ].includes(field.fieldtype)
                        )
                        .sort((a, b) => a.sortorder - b.sortorder)
                        .map((field) => (
                            <div className={`${field.fieldtype === "label" ? "col-span-2" : "col-span-1"}`} key={field.displayid}>
                                <label className={`block ${field.fieldtype === "label" ? "font-bold text-base text-xl w-full" : "text-gray-700 font-semibold"} mb-3`}>
                                    {field.required === "Y" && <span className="text-[#711515]">*</span>} {field.displayname}
                                    {field.tooltip && (
                                        <div className="inline-block relative ml-2 group">
                                            <svg
                                                className="w-5 h-5 text-gray-400 cursor-help"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            </svg>
                                            <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 w-64 p-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                                                {field.tooltip}
                                                <div className="absolute left-1/2 transform -translate-x-1/2 top-full w-2 h-2 bg-gray-800 rotate-45"></div>
                                            </div>
                                        </div>
                                    )}
                                </label>

                                {/* ADDED: Month and Year field handling */}
                                {field.displayname === "Month" ? (
                                    <div className="relative">
                                        <select
                                            name={field.fieldname}
                                            value={formData[field.fieldname] || defaultDate.month}
                                            onChange={handleChange}
                                            className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent appearance-none"
                                            required={field.required === "Y"}
                                        >
                                            <option value="">Select Month</option>
                                            {monthOptions.map(month => (
                                                <option key={month} value={month}>{month}</option>
                                            ))}
                                        </select>
                                        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-700">
                                            <svg
                                                className="h-4 w-4"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                        </div>
                                    </div>
                                ) : field.displayname === "Year" ? (
                                    <div className="relative">
                                        <select
                                            name={field.fieldname}
                                            value={formData[field.fieldname] || defaultDate.year}
                                            onChange={handleChange}
                                            className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent appearance-none"
                                            required={field.required === "Y"}
                                        >
                                            <option value="">Select Year</option>
                                            {yearOptions.map(year => (
                                                <option key={year} value={year}>{year}</option>
                                            ))}
                                        </select>
                                        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-700">
                                            <svg
                                                className="h-4 w-4"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                        </div>
                                    </div>
                                ) : field.fieldtype === "lookup" ? (
                                    <div className="relative">
                                        <input
                                            type="text"
                                            name={field.fieldname}
                                            value={inputValue}
                                            onChange={handleChange}
                                            className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                            style={{
                                                width: '100%'
                                            }}
                                            required={field.required === "Y"}
                                            maxLength={field.maxlength || ""}
                                            autoComplete="off"
                                            ref={inputRef}
                                        />
                                        {showZipDropdown && field.fieldname === 'frc' && (
                                            <div
                                                className="absolute z-10 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                                                style={{ width: inputRef.current ? inputRef.current.offsetWidth : '100%' }}
                                            >
                                                {zipCodes.length > 0 ? (
                                                    zipCodes.map((zip) => (
                                                        <div
                                                            key={zip.itemid}
                                                            className="p-3 hover:bg-gray-100 cursor-pointer"
                                                            onClick={() => handleZipSelection(zip)}
                                                        >
                                                            {zip.zipcode}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div className="p-3 text-gray-500">No results found.</div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ) : field.fieldtype === "text" || field.fieldtype === "numeric" || field.fieldtype === "currency" || field.fieldtype === "textdate" ? (
                                    <>
                                        <input
                                            type="text"
                                            name={field.fieldname}
                                            value={formData[field.fieldname] || ""}
                                            onChange={
                                                field.onblur === "formatPhone(this);"
                                                    ? formatPhoneOnChange
                                                    : field.onfocus === "formatSSN(this);"
                                                        ? formatSSNOnChange
                                                        : handleChange
                                            }
                                            className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                            style={{
                                                width: '100%'
                                            }}
                                            required={field.required === "Y"}
                                            maxLength={field.maxlength || ""}
                                        />
                                        {field.displayname === "Email Address" && emailError && (
                                            <p className="text-[#711515] text-sm mt-2">{emailError}</p>
                                        )}
                                    </>
                                ) : field.fieldtype === "textarea" ? (
                                    <textarea
                                        name={field.fieldname}
                                        value={formData[field.fieldname] || ""}
                                        onChange={handleChange}
                                        className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                        required={field.required === "Y"}
                                        rows="4"
                                    ></textarea>
                                ) : field.fieldtype === "picklist" ? (
                                    <div className="flex flex-col w-full">
                                        <div className="relative w-full">
                                            <select
                                                name={field.fieldname}
                                                value={formData[field.fieldname] || ""}
                                                onChange={handleChange}
                                                className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent appearance-none"
                                                required={field.required === "Y"}
                                            >
                                                <option value="">Select {field.displayname}</option>
                                                {picklistCache[field.itemvaluegroup]?.isLoading ? (
                                                    <option disabled>Loading options...</option>
                                                ) : (
                                                    (picklistCache[field.itemvaluegroup]?.data || []).map((option) => (
                                                        <option key={option.valueid} value={option.valuetext}>
                                                            {option.valuetext}
                                                        </option>
                                                    ))
                                                )}
                                            </select>
                                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-700">
                                                <svg
                                                    className="h-4 w-4"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 20 20"
                                                    fill="currentColor"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>
                                            </div>
                                        </div>
                                        {picklistCache[field.itemvaluegroup]?.error && (
                                            <p className="text-red-500 text-sm mt-1">
                                                {picklistCache[field.itemvaluegroup].error}
                                            </p>
                                        )}
                                    </div>
                                ) : field.fieldtype === "datetimepl" ? (
                                    <div className="w-full">
                                        <DatePicker
                                            selected={formData[field.fieldname] ? new Date(formData[field.fieldname]) : null}
                                            onChange={(date) =>
                                                setFormData({
                                                    ...formData,
                                                    [field.fieldname]: date.toISOString()
                                                })
                                            }
                                            showTimeSelect
                                            timeFormat="HH:mm"
                                            timeIntervals={15}
                                            dateFormat="MMMM d, yyyy h:mm aa"
                                            className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                            required={field.required === "Y"}
                                            placeholderText="Select date and time"
                                        />
                                    </div>
                                ) : field.fieldtype === "date" ? (
                                    <input
                                        type="date"
                                        name={field.fieldname}
                                        value={formData[field.fieldname] || ""}
                                        onChange={handleChange}
                                        className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                        required={field.required === "Y"}
                                    />
                                ) : field.fieldtype === "checkbox" ? (
                                    <CheckboxField
                                        field={field}
                                        formData={formData}
                                        handleCheckboxGroupChange={handleCheckboxGroupChange}
                                        picklistCache={picklistCache}
                                        setPicklistCache={setPicklistCache}
                                    />
                                ) : field.fieldtype === "radio" ? (
                                    <RadioField
                                        field={field}
                                        formData={formData}
                                        handleChange={handleChange}
                                        picklistCache={picklistCache}
                                        setPicklistCache={setPicklistCache}
                                    />
                                ) : null}
                            </div>
                        ))}
                </div>
            </div>


            {/* Tax Rates Table */}
            <div className="mb-10">
                <h3 className="text-xl font-bold text-gray-800 mb-5 flex items-center">
                    <svg
                        className="w-6 h-6 text-[#711515] mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z"
                        />
                    </svg>
                    Current Tax Rates
                </h3>

                <div className="bg-gradient-to-r from-[#f9f9f9] to-[#f0f0f0] p-1 rounded-xl shadow-lg">
                    <div className="overflow-hidden rounded-lg border border-gray-200 shadow-sm">
                        {/* Mobile view (cards) - shown on small screens */}
                        <div className="sm:hidden">
                            {taxTypeOptions.map((option, index) => {
                                // Get dynamic rate from API or fallback to N/A
                                const rate = displayTaxRates[option.dval] || "N/A";

                                // Determine color based on rate value
                                let rateColor = "text-gray-500";
                                const rateValue = parseFloat(rate);
                                if (!isNaN(rateValue)) {
                                    if (rateValue >= 10) rateColor = "text-orange-600";
                                    else if (rateValue >= 7) rateColor = "text-red-600";
                                    else if (rateValue >= 3) rateColor = "text-purple-600";
                                    else if (rateValue >= 2) rateColor = "text-blue-600";
                                    else if (rateValue >= 1) rateColor = "text-green-600";
                                }
                                return (
                                    <div key={index} className="p-4 border-b border-gray-200 bg-white">
                                        <div className="flex items-center mb-2">
                                            <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-md bg-[#711515]/10 text-[#711515]">
                                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth="2"
                                                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                    />
                                                </svg>
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-gray-900">{option.dval}</div>
                                            </div>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-xs text-gray-500">Rate</span>
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${rateColor} bg-opacity-10`}>
                                                {rate}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Desktop view (table) - shown on medium screens and up */}
                        <table className="hidden sm:table min-w-full divide-y divide-gray-200">
                            <thead className="bg-[#711515]">
                                <tr>
                                    <th
                                        scope="col"
                                        className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider"
                                    >
                                        <div className="flex items-center">
                                            Tax Type
                                            <svg
                                                className="ml-1 w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="M19 9l-7 7-7-7"
                                                />
                                            </svg>
                                        </div>
                                    </th>
                                    <th
                                        scope="col"
                                        className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider"
                                    >
                                        <div className="flex items-center">
                                            Rate
                                            <svg
                                                className="ml-1 w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="M19 9l-7 7-7-7"
                                                />
                                            </svg>
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {taxTypeOptions.map((option, index) => {
                                    // Get dynamic rate from API or fallback to N/A
                                    const rate = displayTaxRates[option.dval] || "N/A";

                                    // Determine color based on rate value
                                    let rateColor = "text-gray-500";
                                    const rateValue = parseFloat(rate);
                                    if (!isNaN(rateValue)) {
                                        if (rateValue >= 10) rateColor = "text-orange-600";
                                        else if (rateValue >= 7) rateColor = "text-red-600";
                                        else if (rateValue >= 3) rateColor = "text-purple-600";
                                        else if (rateValue >= 2) rateColor = "text-blue-600";
                                        else if (rateValue >= 1) rateColor = "text-green-600";
                                    }
                                    return (
                                        <tr
                                            key={index}
                                            className="hover:bg-gray-50 transition-colors duration-150"
                                        >
                                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-md bg-[#711515]/10 text-[#711515]">
                                                        <svg
                                                            className="w-6 h-6"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth="2"
                                                                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                            />
                                                        </svg>
                                                    </div>
                                                    <div className="ml-4">
                                                        <div className="text-sm font-medium text-gray-900">{option.dval}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${rateColor} bg-opacity-10`}>
                                                    {rate}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-8">
                {/* Section Groups (Split Payment + Exemption) */}
                {sectionGroups.map((group) => (
                    <div key={group.id} className="border border-gray-200 rounded-lg p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold text-gray-800">Line {group.id}</h3>
                            {group.id > 1 && (
                                <button
                                    type="button"
                                    onClick={() => handleRemoveSectionGroup(group.id)}
                                    className="text-red-500 hover:text-red-700 font-medium"
                                >
                                    Remove Line
                                </button>
                            )}
                        </div>

                        {/* Split Payment Information - ALL FIELDS IN ONE ROW */}
                        <div className="mb-8">
                            <h4 className="text-lg font-bold text-gray-700 border-b border-gray-300 pb-2 mb-4">
                                {group.splitpayment.title}
                            </h4>

                            {/* Single row container with horizontal scroll for mobile */}
                            <div className="overflow-x-auto">
                                <div className="flex flex-nowrap gap-4 min-w-max pb-4">
                                    {group.splitpayment.fields
                                        .filter(
                                            (field) =>
                                                ![
                                                    "itemid",
                                                    "autonumber",
                                                    "readonlydate",
                                                    "readonlypicklist",
                                                    "readonly",
                                                    "hidden",
                                                    "readonlylookup",
                                                    "linkedfield",
                                                    "inlinequery",
                                                    "person",
                                                ].includes(field.fieldtype)
                                        )
                                        .sort((a, b) => a.sortorder - b.sortorder)
                                        .map((field) => (
                                            <div key={`split_${group.id}_${field.displayid}`} className="flex-shrink-0 w-48">
                                                <label className="block text-gray-700 font-semibold mb-2 text-sm">
                                                    {field.required === "Y" && <span className="text-[#711515]">*</span>} {field.displayname}
                                                    {field.tooltip && (
                                                        <div className="inline-block relative ml-1 group">
                                                            <svg
                                                                className="w-4 h-4 text-gray-400 cursor-help"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth="2"
                                                                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                                />
                                                            </svg>
                                                            <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 w-48 p-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                                                                {field.tooltip}
                                                                <div className="absolute left-1/2 transform -translate-x-1/2 top-full w-2 h-2 bg-gray-800 rotate-45"></div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </label>

                                                {field.displayname === "Tax Type" ? (
                                                    <div className="relative">
                                                        <select
                                                            name={`${field.fieldname}_${group.id}`}
                                                            value={formData[`${field.fieldname}_${group.id}`] || ""}
                                                            onChange={handleChange}
                                                            className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent appearance-none text-sm"
                                                            required={field.required === "Y"}
                                                        >
                                                            <option value="">Select Tax Type</option>
                                                            {taxTypeOptions.map((option, index) => (
                                                                <option key={index} value={option.sval}>
                                                                    {option.dval}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-gray-700">
                                                            <svg
                                                                className="h-3 w-3"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                viewBox="0 0 20 20"
                                                                fill="currentColor"
                                                            >
                                                                <path
                                                                    fillRule="evenodd"
                                                                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                                    clipRule="evenodd"
                                                                />
                                                            </svg>
                                                        </div>
                                                    </div>
                                                ) : field.fieldtype === "date" ? (
                                                    <input
                                                        type="date"
                                                        name={`${field.fieldname}_${group.id}`}
                                                        value={formData[`${field.fieldname}_${group.id}`] || ""}
                                                        onChange={handleChange}
                                                        className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent text-sm"
                                                        required={field.required === "Y"}
                                                    />
                                                ) : field.displayname === "Gross Sales Reported" ? (
                                                    <input
                                                        type="number"
                                                        name={`${field.fieldname}_${group.id}`}
                                                        value={formData[`${field.fieldname}_${group.id}`] || ""}
                                                        onChange={handleChange}
                                                        className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent text-sm"
                                                        required={field.required === "Y"}
                                                        step="0.01"
                                                        min="0"
                                                    />
                                                ) : field.displayname === "Exempt / Excluded Reported" ? (
                                                    <input
                                                        type="number"
                                                        name={`${field.fieldname}_${group.id}`}
                                                        value={formData[`${field.fieldname}_${group.id}`] || ""}
                                                        className="w-full p-3 border-2 border-gray-200 rounded-lg bg-gray-100 text-sm"
                                                        readOnly
                                                        step="0.01"
                                                        min="0"
                                                    />
                                                ) : field.displayname === "Net Taxable Reported" ? (
                                                    <input
                                                        type="number"
                                                        name={`${field.fieldname}_${group.id}`}
                                                        value={formData[`${field.fieldname}_${group.id}`] || ""}
                                                        className="w-full p-3 border-2 border-gray-200 rounded-lg bg-gray-100 text-sm"
                                                        readOnly
                                                        step="0.01"
                                                    />
                                                ) : field.displayname === "Reported Tax Amount Due" ? (
                                                    <input
                                                        type="number"
                                                        name={`${field.fieldname}_${group.id}`}
                                                        value={formData[`${field.fieldname}_${group.id}`] || ""}
                                                        className="w-full p-3 border-2 border-gray-200 rounded-lg bg-gray-100 text-sm"
                                                        readOnly
                                                        step="0.01"
                                                    />
                                                ) : field.fieldtype === "text" || field.fieldtype === "numeric" || field.fieldtype === "currency" || field.fieldtype === "textdate" ? (
                                                    <input
                                                        type="text"
                                                        name={`${field.fieldname}_${group.id}`}
                                                        value={formData[`${field.fieldname}_${group.id}`] || ""}
                                                        onChange={handleChange}
                                                        className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent text-sm"
                                                        required={field.required === "Y"}
                                                        maxLength={field.maxlength || ""}
                                                    />
                                                ) : field.fieldtype === "textarea" ? (
                                                    <textarea
                                                        name={`${field.fieldname}_${group.id}`}
                                                        value={formData[`${field.fieldname}_${group.id}`] || ""}
                                                        onChange={handleChange}
                                                        className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent text-sm"
                                                        required={field.required === "Y"}
                                                        rows="2"
                                                    ></textarea>
                                                ) : field.fieldtype === "picklist" ? (
                                                    <div className="relative">
                                                        <select
                                                            name={`${field.fieldname}_${group.id}`}
                                                            value={formData[`${field.fieldname}_${group.id}`] || ""}
                                                            onChange={handleChange}
                                                            className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent appearance-none text-sm"
                                                            required={field.required === "Y"}
                                                        >
                                                            <option value="">Select {field.displayname}</option>
                                                            {picklistCache[field.itemvaluegroup]?.data?.map((option) => (
                                                                <option key={option.valueid} value={option.valuetext}>
                                                                    {option.valuetext}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-gray-700">
                                                            <svg
                                                                className="h-3 w-3"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                viewBox="0 0 20 20"
                                                                fill="currentColor"
                                                            >
                                                                <path
                                                                    fillRule="evenodd"
                                                                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                                    clipRule="evenodd"
                                                                />
                                                            </svg>
                                                        </div>
                                                    </div>
                                                ) : field.fieldtype === "checkbox" ? (
                                                    <CheckboxField
                                                        field={field}
                                                        formData={formData}
                                                        handleCheckboxGroupChange={handleCheckboxGroupChange}
                                                        picklistCache={picklistCache}
                                                        setPicklistCache={setPicklistCache}
                                                        groupId={group.id}
                                                    />
                                                ) : field.fieldtype === "radio" ? (
                                                    <RadioField
                                                        field={field}
                                                        formData={formData}
                                                        handleChange={handleChange}
                                                        picklistCache={picklistCache}
                                                        setPicklistCache={setPicklistCache}
                                                        groupId={group.id}
                                                    />
                                                ) : null}
                                            </div>
                                        ))}
                                </div>
                            </div>
                        </div>

                        {/* Exemption Information */}
                        <div>
                            <h4 className="text-lg font-bold text-gray-700 border-b border-gray-300 pb-2 mb-4">
                                {group.exemption.title}
                            </h4>
                            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded mb-4">
                                <p className="text-gray-700 font-medium">
                                    Instructions: The total of exemptions/exclusions must match the amount in the Reported Exempt Sales field above.
                                </p>
                            </div>

                            {group.exemptionGroups.map(exemptionId => (
                                <div key={`exemption_${group.id}_${exemptionId}`} className="border border-gray-200 rounded-lg p-6 mb-6">
                                    <div className="flex justify-between items-center mb-4">
                                        <h5 className="text-md font-bold text-gray-700">
                                            Exemption {exemptionId}
                                        </h5>
                                        {group.exemptionGroups.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveExemptionGroup(group.id, exemptionId)}
                                                className="text-red-500 hover:text-red-700 font-medium"
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {group.exemption.fields
                                            .filter(
                                                (field) =>
                                                    ![
                                                        "itemid",
                                                        "autonumber",
                                                        "readonlydate",
                                                        "readonlypicklist",
                                                        "readonly",
                                                        "hidden",
                                                        "readonlylookup",
                                                        "linkedfield",
                                                        "inlinequery",
                                                        "person",
                                                    ].includes(field.fieldtype)
                                            )
                                            .sort((a, b) => a.sortorder - b.sortorder)
                                            .map((field) => (
                                                <div className={`${field.fieldtype === "label" ? "col-span-2" : "col-span-1"}`} key={`${group.id}_${exemptionId}_${field.displayid}`}>
                                                    <label className={`block ${field.fieldtype === "label" ? "font-bold text-base text-xl w-full" : "text-gray-700 font-semibold"} mb-3`}>
                                                        {field.required === "Y" && <span className="text-[#711515]">*</span>} {field.displayname}
                                                        {field.tooltip && (
                                                            <div className="inline-block relative ml-2 group">
                                                                <svg
                                                                    className="w-5 h-5 text-gray-400 cursor-help"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    viewBox="0 0 24 24"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                >
                                                                    <path
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        strokeWidth="2"
                                                                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                                    />
                                                                </svg>
                                                                <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 w-64 p-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                                                                    {field.tooltip}
                                                                    <div className="absolute left-1/2 transform -translate-x-1/2 top-full w-2 h-2 bg-gray-800 rotate-45"></div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </label>

                                                    {field.fieldname === exemptionTaxTypeFieldName ? (
                                                        <div className="relative">
                                                            <select
                                                                name={`${field.fieldname}_${group.id}_${exemptionId}`}
                                                                value={formData[`${field.fieldname}_${group.id}_${exemptionId}`] || ""}
                                                                onChange={handleChange}
                                                                className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent appearance-none"
                                                                required={field.required === "Y"}
                                                            >
                                                                <option value="">Select Tax Type</option>
                                                                {taxTypeOptions.map((option, index) => (
                                                                    <option key={index} value={option.sval}>
                                                                        {option.dval}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-700">
                                                                <svg
                                                                    className="h-4 w-4"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    viewBox="0 0 20 20"
                                                                    fill="currentColor"
                                                                >
                                                                    <path
                                                                        fillRule="evenodd"
                                                                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                                        clipRule="evenodd"
                                                                    />
                                                                </svg>
                                                            </div>
                                                        </div>
                                                    ) : field.displayname === "Exemption Code" ? (
                                                        <div className="relative">
                                                            <select
                                                                name={`${field.fieldname}_${group.id}_${exemptionId}`}
                                                                value={formData[`${field.fieldname}_${group.id}_${exemptionId}`] || ""}
                                                                onChange={handleChange}
                                                                onClick={() => {
                                                                    const taxTypeKey = `${splitPaymentFieldNames['Tax Type']}_${group.id}`;
                                                                    const taxType = formData[taxTypeKey] || '';
                                                                    if (taxType) {
                                                                        fetchExemptionCodes(taxType, group.id, exemptionId);
                                                                    }
                                                                }}
                                                                disabled={!formData[`${splitPaymentFieldNames['Tax Type']}_${group.id}`]}
                                                                className={`w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent appearance-none ${!formData[`${splitPaymentFieldNames['Tax Type']}_${group.id}`]
                                                                    ? 'bg-gray-100 cursor-not-allowed'
                                                                    : ''
                                                                    }`}
                                                                required={field.required === "Y"}
                                                            >
                                                                <option value="">
                                                                    {!formData[`${splitPaymentFieldNames['Tax Type']}_${group.id}`]
                                                                        ? "Select Tax Type first"
                                                                        : "Select Exemption Code"
                                                                    }
                                                                </option>
                                                                {exemptionCodeOptions[`${group.id}_${exemptionId}`]?.map((option, index) => (
                                                                    <option key={index} value={option.sval}>
                                                                        {option.dval}
                                                                    </option>
                                                                )) || []}
                                                            </select>
                                                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-700">
                                                                <svg
                                                                    className="h-4 w-4"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    viewBox="0 0 20 20"
                                                                    fill="currentColor"
                                                                >
                                                                    <path
                                                                        fillRule="evenodd"
                                                                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                                        clipRule="evenodd"
                                                                    />
                                                                </svg>
                                                            </div>
                                                        </div>
                                                    ) : field.displayname === "Exemption Amount" ? (
                                                        <input
                                                            type="number"
                                                            name={`${field.fieldname}_${group.id}_${exemptionId}`}
                                                            value={formData[`${field.fieldname}_${group.id}_${exemptionId}`] || ""}
                                                            onChange={handleChange}
                                                            className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                                            required={field.required === "Y"}
                                                            step="0.01"
                                                            min="0"
                                                        />
                                                    ) : field.fieldtype === "text" || field.fieldtype === "numeric" || field.fieldtype === "currency" ? (
                                                        <input
                                                            type="text"
                                                            name={`${field.fieldname}_${group.id}_${exemptionId}`}
                                                            value={formData[`${field.fieldname}_${group.id}_${exemptionId}`] || ""}
                                                            onChange={handleChange}
                                                            className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent"
                                                            required={field.required === "Y"}
                                                        />
                                                    ) : field.fieldtype === "picklist" ? (
                                                        <div className="relative">
                                                            <select
                                                                name={`${field.fieldname}_${group.id}_${exemptionId}`}
                                                                value={formData[`${field.fieldname}_${group.id}_${exemptionId}`] || ""}
                                                                onChange={handleChange}
                                                                className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent appearance-none"
                                                                required={field.required === "Y"}
                                                            >
                                                                <option value="">Select {field.displayname}</option>
                                                                {picklistCache[field.itemvaluegroup]?.data?.map((option) => (
                                                                    <option key={option.valueid} value={option.valuetext}>
                                                                        {option.valuetext}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-700">
                                                                <svg
                                                                    className="h-4 w-4"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    viewBox="0 0 20 20"
                                                                    fill="currentColor"
                                                                >
                                                                    <path
                                                                        fillRule="evenodd"
                                                                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                                        clipRule="evenodd"
                                                                    />
                                                                </svg>
                                                            </div>
                                                        </div>
                                                    ) : null}
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            ))}

                            <button
                                type="button"
                                onClick={() => handleAddExemptionGroup(group.id)}
                                className="flex items-center justify-center bg-[#711515] text-white px-4 py-2 rounded-lg hover:bg-[#5a1010] transition mt-4"
                            >
                                <svg
                                    className="w-5 h-5 mr-2"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                Add More Exemptions
                            </button>
                        </div>

                        {/* Tobacco Details - Only show when Tax Type is "Tobacco Products Tax" - ALL FIELDS IN ONE ROW */}
                        {(() => {
                            const taxTypeKey = `${splitPaymentFieldNames['Tax Type']}_${group.id}`;
                            const selectedTaxType = formData[taxTypeKey];
                            const isTobaccoTax = taxTypeOptions.find(opt => opt.sval === selectedTaxType && opt.dval === "Tobacco Products Tax");

                            return isTobaccoTax ? (
                                <div className="mt-6">
                                    <h4 className="text-lg font-bold text-gray-700 border-b border-gray-300 pb-2 mb-4">
                                        {group.tobaccodetails.title}
                                    </h4>
                                    <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded mb-4">
                                        <p className="text-gray-700 font-medium">
                                            Please provide additional tobacco product details for this tax return.
                                        </p>
                                    </div>

                                    {/* All fields in one horizontal row */}
                                    <div className="overflow-x-auto">
                                        <div className="flex flex-nowrap gap-4 min-w-max pb-4">
                                            {group.tobaccodetails.fields
                                                .filter(
                                                    (field) =>
                                                        ![
                                                            "itemid",
                                                            "autonumber",
                                                            "readonlydate",
                                                            "readonlypicklist",
                                                            "readonly",
                                                            "hidden",
                                                            "readonlylookup",
                                                            "linkedfield",
                                                            "inlinequery",
                                                            "person",
                                                        ].includes(field.fieldtype)
                                                )
                                                .sort((a, b) => a.sortorder - b.sortorder)
                                                .map((field) => (
                                                    <div key={`tobacco_${group.id}_${field.displayid}`} className="flex-shrink-0 w-48">
                                                        <label className="block text-gray-700 font-semibold mb-2 text-sm">
                                                            {field.required === "Y" && <span className="text-[#711515]">*</span>} {field.displayname}
                                                            {field.tooltip && (
                                                                <div className="inline-block relative ml-1 group">
                                                                    <svg
                                                                        className="w-4 h-4 text-gray-400 cursor-help"
                                                                        fill="none"
                                                                        stroke="currentColor"
                                                                        viewBox="0 0 24 24"
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                    >
                                                                        <path
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                            strokeWidth="2"
                                                                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                                        />
                                                                    </svg>
                                                                    <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 w-48 p-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                                                                        {field.tooltip}
                                                                        <div className="absolute left-1/2 transform -translate-x-1/2 top-full w-2 h-2 bg-gray-800 rotate-45"></div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </label>

                                                        {field.fieldtype === "date" ? (
                                                            <input
                                                                type="date"
                                                                name={`${field.fieldname}_${group.id}`}
                                                                value={formData[`${field.fieldname}_${group.id}`] || ""}
                                                                onChange={handleChange}
                                                                className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent text-sm"
                                                                required={field.required === "Y"}
                                                            />
                                                        ) : field.fieldtype === "text" || field.fieldtype === "numeric" || field.fieldtype === "currency" || field.fieldtype === "textdate" ? (
                                                            <input
                                                                type={field.fieldtype === "numeric" || field.fieldtype === "currency" ? "number" : "text"}
                                                                name={`${field.fieldname}_${group.id}`}
                                                                value={formData[`${field.fieldname}_${group.id}`] || ""}
                                                                onChange={handleChange}
                                                                className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent text-sm"
                                                                required={field.required === "Y"}
                                                                maxLength={field.maxlength || ""}
                                                                step={field.fieldtype === "currency" ? "0.01" : undefined}
                                                            />
                                                        ) : field.fieldtype === "textarea" ? (
                                                            <textarea
                                                                name={`${field.fieldname}_${group.id}`}
                                                                value={formData[`${field.fieldname}_${group.id}`] || ""}
                                                                onChange={handleChange}
                                                                className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent text-sm"
                                                                required={field.required === "Y"}
                                                                rows="2"
                                                            ></textarea>
                                                        ) : field.fieldtype === "picklist" ? (
                                                            <div className="relative">
                                                                <select
                                                                    name={`${field.fieldname}_${group.id}`}
                                                                    value={formData[`${field.fieldname}_${group.id}`] || ""}
                                                                    onChange={handleChange}
                                                                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent appearance-none text-sm"
                                                                    required={field.required === "Y"}
                                                                >
                                                                    <option value="">Select {field.displayname}</option>
                                                                    {picklistCache[field.itemvaluegroup]?.data?.map((option) => (
                                                                        <option key={option.valueid} value={option.valuetext}>
                                                                            {option.valuetext}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-gray-700">
                                                                    <svg
                                                                        className="h-3 w-3"
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        viewBox="0 0 20 20"
                                                                        fill="currentColor"
                                                                    >
                                                                        <path
                                                                            fillRule="evenodd"
                                                                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                                            clipRule="evenodd"
                                                                        />
                                                                    </svg>
                                                                </div>
                                                            </div>
                                                        ) : field.fieldtype === "checkbox" ? (
                                                            <CheckboxField
                                                                field={field}
                                                                formData={formData}
                                                                handleCheckboxGroupChange={handleCheckboxGroupChange}
                                                                picklistCache={picklistCache}
                                                                setPicklistCache={setPicklistCache}
                                                                groupId={group.id}
                                                            />
                                                        ) : field.fieldtype === "radio" ? (
                                                            <RadioField
                                                                field={field}
                                                                formData={formData}
                                                                handleChange={handleChange}
                                                                picklistCache={picklistCache}
                                                                setPicklistCache={setPicklistCache}
                                                                groupId={group.id}
                                                            />
                                                        ) : null}
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                </div>
                            ) : null;
                        })()}
                    </div>
                ))}

                {/* Line 8 - Total Tax Due */}
                <div className="col-span-2 mt-0 mb-0">
                    <div className="py-1 rounded-lg bg-white">
                        <div className="grid grid-cols-12 gap-2 items-center">
                            <div className="col-span-9">
                                <label className="block text-gray-700 font-semibold mb-0 text-sm">
                                    Add lines 1 - 7 in Column 5. This is your <strong>TOTAL TAX DUE</strong> . . . .
                                </label>
                            </div>
                            <div className="col-span-3">
                                <input
                                    type="number"
                                    name="totalTaxDue"
                                    value={formData.totalTaxDue || "0.00"}
                                    readOnly
                                    className="w-full p-3 border-2 border-gray-200 rounded-lg bg-blue-50 text-sm font-semibold"
                                    step="0.01"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Line 9 - Payment Amount */}
                <div className="col-span-2 mb-0">
                    <div className="py-1 rounded-lg bg-white">
                        <div className="grid grid-cols-12 gap-2 items-center">
                            <div className="col-span-9">
                                <label className="block text-gray-700 font-semibold mb-0 text-sm">
                                    ENTER AMOUNT YOU ARE PAYING WITH THIS RETURN . . . .
                                </label>
                            </div>
                            <div className="col-span-3">
                                <input
                                    type="number"
                                    name="paymentAmount"
                                    value={formData.paymentAmount || formData.totalTaxDue || "0.00"}
                                    onChange={handleChange}
                                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#711515] focus:border-transparent bg-blue-50 text-sm"
                                    step="0.01"
                                    min="0"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Add Section Group Button */}
                <button
                    type="button"
                    onClick={handleAddSectionGroup}
                    className="flex items-center justify-center bg-[#711515] text-white px-6 py-3 rounded-lg hover:bg-[#5a1010] transition"
                >
                    <svg
                        className="w-5 h-5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Another Line
                </button>

                {/* Signature Section */}
                <div className="col-span-2 mt-8 mb-6">
                    <div className="border border-gray-300 p-6 rounded-lg bg-white">
                        {/* Tax Return Information Text */}
                        <div className="mb-6 space-y-4 text-sm text-gray-700">
                            <div>
                                <p className="font-bold mb-2">CREDITS:</p>
                                <p>If a credit or refund is due, the Department will calculate the amount and issue a notice of refund.</p>
                            </div>

                            <div>
                                <p className="font-bold mb-2">PENALTIES AND INTEREST:</p>
                                <p>Late tax returns, late payments, or underpayment of tax due will result in the Department calculating and applying penalties and interest, followed by a notice of deficiencies.</p>
                            </div>

                            <div>
                                <p className="font-bold mb-2">TAX RETURN DUE DATE AND FILING:</p>
                                <p>This tax return MUST BE SIGNED and FILED with Schedule A (if applicable) and payment by the due date to avoid penalties and interest. If mailed, the return must be postmarked by the due date. If the due date falls on a weekend or legal holiday, the due date shall be the next business day.</p>
                            </div>

                            <div>
                                <p className="font-bold mb-2">DECLARATION OF PREPARER (OTHER THAN TAXPAYER):</p>
                                <p className="mb-2">I DECLARE, UNDER PENALTY OF PERJURY, THAT I HAVE EXAMINED THIS RETURN AND THE INFORMATION CONTAINED HEREIN, INCLUDING ACCOMPANYING SCHEDULES AND STATEMENTS, AND TO THE BEST OF MY KNOWLEDGE AND BELIEF THEY ARE TRUE, CORRECT AND COMPLETE.</p>
                                <p>DECLARATION OF PREPARER (OTHER THAN TAXPAYER) IS BASED ON ALL INFORMATION OF WHICH PREPARER HAS ANY KNOWLEDGE.</p>
                            </div>
                        </div>

                        <h3 className="text-xl font-bold mb-4 text-gray-800 flex items-center">
                            <svg
                                className="w-6 h-6 text-[#711515] mr-2"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                />
                            </svg>
                            Signature Required
                            <span className="text-red-500 ml-2">*</span>
                        </h3>

                        <div className="mb-4">
                            <label className="font-bold block mb-2 text-gray-700 text-sm">
                                Sign below to certify the accuracy of this report:
                            </label>
                            <div
                                id="signature-container-taxreturn"
                                className="border-2 border-gray-300 rounded-lg p-4 bg-white"
                                style={{ minHeight: '200px' }}
                            >
                                <SignatureCanvas
                                    ref={sigPad}
                                    penColor="#0000FF"
                                    backgroundColor="rgb(255, 255, 255)"
                                    canvasProps={{
                                        width: canvasSize.width || 500,
                                        height: canvasSize.height || 200,
                                        className: 'signature-canvas w-full border border-gray-400 rounded bg-white cursor-crosshair'
                                    }}
                                    onBegin={handleSignatureBegin}
                                    onEnd={handleSignatureEnd}
                                    clearOnResize={false}
                                />
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                            <div className="text-sm text-gray-600">
                                {isSigned ? (
                                    <span className="text-green-600 font-bold flex items-center">
                                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        Signature provided
                                    </span>
                                ) : (
                                    <span className="text-red-600 flex items-center">
                                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                        Please sign above (Required)
                                    </span>
                                )}
                            </div>

                            <button
                                type="button"
                                onClick={clearSignature}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm font-medium"
                            >
                                Clear Signature
                            </button>
                        </div>
                    </div>
                </div>

                <div className="col-span-2 flex justify-center mt-6">
                    <button
                        type="submit"
                        disabled={isAPILoading || !isSigned}
                        className={`bg-[#711515] text-white px-6 py-3 rounded-lg ${isAPILoading || !isSigned ? "opacity-50 cursor-not-allowed" : "hover:bg-[#5a1010]"} transition`}
                    >
                        {isAPILoading ? "Submitting..." : "Submit"}
                    </button>
                </div>
            </form>

            {/* Confirmation Modal */}
            <ConfirmationModal
                open={showConfirmation}
                onClose={() => setShowConfirmation(false)}
                onConfirm={confirmSubmit}
                formSections={formSections}
                sectionGroups={sectionGroups}
                formData={formData}
            />

            {/* Download Popup */}
            {showDownloadPopup && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 py-8 px-4 sm:px-6 lg:px-8">
                    <div className="max-w-md mx-auto bg-white shadow-xl rounded-lg overflow-hidden border border-gray-200 text-center">
                        <div className="bg-[#840028] text-white p-6">
                            <h1 className="text-2xl font-bold">Colorado River Indian Tribes</h1>
                        </div>

                        <div className="p-8">
                            <div className="flex justify-center mb-6">
                                <svg className="h-16 w-16 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                            </div>

                            <h3 className="text-2xl font-bold text-gray-800 mb-4">Tax Return Submitted Successfully!</h3>

                            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                                <p className="text-green-800 font-semibold">
                                    Your tax return has been submitted successfully.
                                </p>

                                <p className="text-green-700 text-sm">
                                    Reference: <span className="font-bold">Tax Return</span>
                                </p>
                            </div>

                            <p className="text-gray-600 mb-6 text-sm">
                                Thank you for submitting your tax return.
                                Your return has been received and is being processed.
                                You can download a detailed PDF copy for your records.
                            </p>

                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <button
                                    onClick={async () => {
                                        if (submittedData) {
                                            const pdfDoc = await generatePDF(
                                                submittedData.formData, 
                                                submittedData.sectionGroups, 
                                                submittedData.taxPayerName,
                                                submittedData.signatureDataURL || null
                                            );
                                            pdfDoc.save("CRIT-TR1-Tax-Return.pdf");
                                        }
                                        setShowDownloadPopup(false);
                                    }}
                                    className="bg-blue-600 text-white px-2 py-3 rounded-lg font-semibold hover:bg-blue-700 transition duration-200 shadow-md flex items-center justify-center gap-2"
                                >
                                    <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    Download Tax Return PDF
                                </button>

                                <button
                                    onClick={() => setShowDownloadPopup(false)}
                                    className="bg-[#840028] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#6a0020] transition duration-200 shadow-md"
                                >
                                    Close
                                </button>
                            </div>

                            <p className="text-xs text-gray-500 mt-4">
                                PDF includes all tax return details with signatures and field values.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Custom CSS for signature canvas */}
            <style>{`
                .signature-canvas {
                    cursor: crosshair !important;
                    touch-action: none !important;
                    -webkit-touch-callout: none;
                    -webkit-user-select: none;
                    -moz-user-select: none;
                    -ms-user-select: none;
                    user-select: none;
                }
                .signature-canvas:active {
                    cursor: crosshair !important;
                }
                #signature-container-taxreturn {
                    min-height: 200px;
                    position: relative;
                }
                #signature-container-taxreturn canvas {
                    display: block;
                    width: 100% !important;
                    max-width: 100%;
                }
                
                @media (max-width: 640px) {
                    #signature-container-taxreturn {
                        min-height: 150px;
                    }
                }
            `}</style>
        </div>
    );
};

export default TaxReturnForm;